import { loadMap, GameMap, MapJson, Body, Stages, Vec, fieldFor, flowDir } from '../shared/map';
import { createSim, DT, TICK_RATE, Snapshot, Team, Sim, speedFor, radiusFor, moveHuman, freshStamina, Stamina, MoveRules } from '../shared/sim';
import { TileLayer, TreeLayer, BuildingLayer, Surround, drawBorder, drawPois, DrawEnt } from './render';
import { MapArt, Minimap, MapWindow, MapLive, drawPinMark } from './mapview';
import { TrafficLayer } from './traffic';
import { Dressing } from './dressing';
import { Convoy } from './convoy';
import { PeopleLayer, castOf } from './people';
import { StageView, STAGE_LABEL } from './stage';
import { ChaloData, ChaloGeo, ChaloSnap, chaloGeo, chaloMask } from '../shared/chalo';
import { CompoundLayer } from './compounds';
import { toliBodies, drawCordon, CordonProps, checklist, drawChecklist, resultLine, resultIcon, usePrompt, drawMats, drawTalk } from './chalo';
import type { SimOptions } from '../shared/sim';
import { avatarName, isAvatar, roster } from '../shared/avatars';
import { SelectView } from './select';
import { myPick } from '../shared/select-ui';
import { cleanRoomCode, newRoomCode } from '../shared/roomcode';
import {
  JourneyData, Routes, JourneyState, Arrival, OptionId, Choices, startJourney, journeyStep, parts, arrival, markerPoint, journeyCard, clock, toMin, lateText, nearestGate,
  BazaarData, bazaarMin, carryOf,
} from '../shared/journey';
import { RideView } from './ride';
import { Phone, JourneyCardView, BazaarView, DoorstepPoints, prepareDoorstep, pathLine, pathLength, drawPath, drawMarker, stallPoint, stageText, chime } from './story';
import type { Summoned } from './traffic';
import { Doomscroll, LobbyWidget, RoomEvent } from './feed';
import { MetroClient } from './metro';
import { nearestGate as nearestStationGate } from '../shared/train';
import { ChaloWorld } from './chaloworld';
import { Sounds } from './audio';
import { name, gheeUnderFeet } from './names';
import { Announcer, webAudio } from './announce';
import { CrowdCues, blackout, gameProps, drawIcon } from './hud';
import { TitleMap, cityStatus, section144 } from './title';
import { FONT, INK, box, inked, meter, stamp, tag } from './ui';
import { RoamPicker, ROAM_MAP, loadPlaces } from './roam';
import { RoamPlace, roamSpot } from '../shared/roam';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const canvas = $<HTMLCanvasElement>('c');
const ctx = canvas.getContext('2d')!;
const menu = $('menu'), statusEl = $('status');
const playBtn = $<HTMLButtonElement>('play'), roamBtn = $<HTMLButtonElement>('roam'), friendsBtn = $<HTMLButtonElement>('friends');
const inviteEl = $('invite'), inviteCode = $('inviteCode'), inviteCopy = $<HTMLButtonElement>('inviteCopy');
/** a room of friends (docs/plans/2026-10-18-date-raho.md, section 7): from an invite link's ?room=, or made by "Play with friends" */
let roomCode = cleanRoomCode(new URLSearchParams(location.search).get('room'));
const inviteLink = (code: string) => `${location.origin}/play?room=${code}`;
// the waiting lobby (docs/plans/2026-10-18-waiting-lobby.md): the doomscroll before you join, the room card while it fills
const feed = new Doomscroll($('feed'));
const lobbyW = new LobbyWidget($('lobby'), () => leave(''));
/** the lobby room you are in: its number and size (0 offline, or in a room of friends) */
const room = { id: 0, size: 0 };
// the city lobby (docs/plans/2026-10-18-city-lobby.md): a lobby room waits on the district map, dropped at a metro exit,
// and moves to the game's map at the whistle. The game's map and stages are kept to come back to; each city is fetched once
let gameJson: MapJson | null = null, gameStages: Stages | null = null, loadedJson: MapJson | null = null;
const cityJson = new Map<string, MapJson>();
/** map switches queue up; snapshots are dropped while one is pending, since they belong to the map being loaded */
/** the metro exit the lobby dropped you at, for the banner */
let myDrop = '';
let switching = 0, worldQ: Promise<void> = Promise.resolve(), welcomeMode = 'stages';
// the shared metro (docs/plans/2026-10-18-shared-metro.md): a lobby room's players start at different stations, wait on
// a platform and ride one train; a station loads like the city, the platform and the train are drawn by MetroClient
const metroC = new MetroClient((m) => transport?.send(m), chime);
function setWorld(w: 'game' | 'city' | 'station', url?: string): Promise<void> {
  switching++;
  worldQ = worldQ.then(async () => {
    if ((w === 'city' || w === 'station') && url) {
      // a station's forecourt street is ordinary road: no zone tint (as on a doorstep). Both are walkable, so the
      // server's tiles and these agree
      if (!cityJson.has(url)) { const j = await fetchJson<MapJson>(url); cityJson.set(url, w === 'station' ? { ...j, zone: [], rows: j.rows.map((r) => r.replace(/P/g, '=')) } : j); }
      const j = cityJson.get(url)!;
      if (loadedJson !== j) await loadWorld(j, { extras: false });
      stages = gameStages; // the city plays by the game's jog rules (the server's city sim has them too); no stage view
    } else if (w === 'game') {
      gameJson ??= await fetchJson<MapJson>('/map.json');
      if (loadedJson !== gameJson) { await loadWorld(gameJson, { extras: true }); gameStages = stages; }
    }
    chaloOn = welcomeMode === 'chalo' && !!geo;
    P.x = P.y = 0; pending = []; snaps = []; // the first snapshot on the new map snaps you there
  }).catch((err) => { console.error(err); leave('Could not load the map.'); }).finally(() => { switching--; });
  return worldQ;
}

const store = {
  get(k: string, d: string) { try { return localStorage.getItem(k) ?? d; } catch { return d; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};
// the title screen's live operation map, and its dockets (docs/plans/2026-10-18-dharna-144.md)
const rectOf = (el: Element | null) => { const r = el?.getBoundingClientRect(); return r && r.width ? { x0: r.left - 8, y0: r.top - 8, x1: r.right + 14, y1: r.bottom + 14 } : null; };
const titleMap = new TitleMap($<HTMLCanvasElement>('titlemap'), () => !menu.classList.contains('hide'),
  () => { const c = rectOf(document.querySelector('.cmd')); return innerWidth > 900 && c ? { x0: c.x1 + 6, y0: 0, x1: innerWidth, y1: innerHeight } : { x0: 0, y0: 0, x1: innerWidth, y1: innerHeight }; },
  () => ['#corner', '.mapkey', '.cmd', '#strip'].map((q) => rectOf(document.querySelector(q))).filter((r): r is NonNullable<typeof r> => !!r),
  $<HTMLCanvasElement>('minimap'));
void titleMap.load();
section144($('s144'), store);
// the title's foot strip runs from the panel to the legend, wherever the legend starts at this size
const stripFit = () => { const k = document.querySelector('.mapkey')?.getBoundingClientRect(); if (k?.width) document.documentElement.style.setProperty('--strip-r', `${Math.round(innerWidth - k.left + 24)}px`); };
addEventListener('resize', stripFit); stripFit();
const announcer = new Announcer(() => audio.muted, webAudio(() => audio.voiceOut()));
const audio = new Sounds(store, new URLSearchParams(location.search).get('mute') === '1'); // ?mute=1: no sound at all
// the title's sounds: its bed while the menu is up (main loop), the tour's slide and stamp, a stamp for Section 144
// noted, clicks on the panel's buttons, and the sound pill (the same mute as the game's N)
titleMap.sfx = (id) => { if (!menu.classList.contains('hide')) audio.play(id); };
$('s144-ok').addEventListener('click', () => audio.play('stamp'));
document.querySelectorAll<HTMLElement>('#friends, #roam, #menu .ep').forEach((b) => b.addEventListener('click', () => audio.play('click')));
$('play').addEventListener('click', () => audio.play('confirm'));
const sndEl = $<HTMLButtonElement>('snd');
const sndShow = () => { sndEl.setAttribute('aria-pressed', String(!audio.muted)); sndEl.textContent = audio.muted ? 'Sound off' : 'Sound on'; };
sndEl.addEventListener('click', () => { audio.toggle(); sndShow(); }); sndShow();

// ------------------------------------------------------------------ state
interface Transport { send(m: Record<string, unknown>): void; close(): void; pump?(ms: number): void; spend?(seconds: number): void; place?(p: Vec): void }
interface SnapRec { rt: number; rows: Map<number, number[]> }

let map: GameMap;
let compounds: CompoundLayer | null = null; // the walled plots nobody may enter (docs/plans/2026-10-18-no-go.md)
let layer: TileLayer, trees: TreeLayer, blocks: BuildingLayer | null, traffic: TrafficLayer | null, people: PeopleLayer, mini: Minimap, mapWin: MapWindow;
let surround: Surround | null = null; // the streets past the map's edge, dimmed; arrives after the map
/** your pin from the map window, in world px: shown on the street, the minimap and the map until you reach it */
let pin: Vec | null = null;
let transport: Transport | null = null;
let playing = false;
let meId = -1;
/**
 * your face (src/shared/avatars.ts): online it is picked at the station (character select); offline (practice, roam,
 * story) you are a protester with the last protester face you locked in, or a random one. Kept in this browser only
 */
let myAv = (() => {
  const s = store.get('kafila_face', ''), v = s === '' ? NaN : Number(s), ids = roster('P');
  return isAvatar(v) && ids.includes(v) ? v : ids[Math.floor(Math.random() * ids.length)];
})();
/** offline, you are known by your face: its name is the one over your head (docs/plans/2026-10-19-character-select.md) */
const offlineName = () => avatarName(myAv);
/** character select (docs/plans/2026-10-19-character-select.md, section 6): `me` is your rider number on your side */
const selectV = new SelectView($('select'), (m) => transport?.send(m), () => selMe(), (id) => audio.play(id));
/** your rider number, as `sel` and the metro's status give it; else your entity id (a direct room) */
let selNo = -1;
const selMe = () => (selNo >= 0 ? selNo : metroC.st?.me ?? meId);
/** your side: drawn by the server at the gate (docs/plans/2026-10-19-landing-roles.md), told in `welcome` */
let myTeam: Team = 'P';
const P: Body & Stamina & { px: number; py: number } = { x: 0, y: 0, vx: 0, vy: 0, r: 0, px: 0, py: 0, ...freshStamina() }; // r is set from the map at boot
let SPEED = 0, PLAYER_R = 0, NPC_R = 0; // px/s and px, from the map's scale (same functions as the server)
let pending: { s: number; x: number; y: number; r: number }[] = [];
// stage areas and pacing, when the map has a stages file (docs/plans/2026-10-17-pacing.md)
let stages: Stages | null = null, view: StageView | null = null;
const stage = { sg: -1, sv: 0, sgt: 0, k: [0, 0, 0, 0] };
/** story mode's arrival: the player walks in unbounded until inside the stage's area (the sim says so) */
let meFree = false;
// Chalo mode (docs/plans/2026-10-17-chalo.md): on when the server says so, or offline with ?chalo
let chaloData: ChaloData | null = null, geo: ChaloGeo | null = null, chaloOn = false, chs: ChaloSnap | null = null;
/** the police lines standing on the streets, built once per map and its lines */
let cordonProps: { geo: ChaloGeo; props: CordonProps } | null = null;
/** Chalo, from the snapshot: seconds left in the police bus (0: free), and the gate sat in at (-1: standing) */
let meHeld = 0, meSit = -1;
let world: ChaloWorld | null = null; // Chalo's props: barricades, police lines, booths, gates, the stage
let dressing: Dressing | null = null, convoy: Convoy | null = null; // camp scenes and parked autos; the van and bus that leave
let ringRoads: Uint8Array | null = null; // Chalo: the police close every road inside the outer cordon to cars
let cues: CrowdCues | null = null; // Chalo: emotes over the tolis and motion cues (hud.ts)
/** Chalo: the director's blackout now, from the snapshot's clock (null outside Chalo) */
const dark = () => (chaloOn && chaloData && chs ? blackout(chaloData.rules, chs.t) : null);
/** Chalo: the checklist, with distances from you unless the blackout hides them */
function chaloLines() {
  const b = dark(), metre = map.mpt ? map.tile / map.mpt : 15;
  return checklist(chaloData!, geo!, chs!, hud.pt, b?.hides.includes('checklistDistances') ? null : P, metre, b?.left ?? 0);
}
const hintAt = { x: 0, y: 0 };
let castSeen: string[] = [];
let pushT = 0, onScreen = 0, frameMs = 16.7, zoneAt: { x: number; y: number } | null = null;
let seq = 0;
let snaps: SnapRec[] = [];
const names = new Map<number, string>();
const hud = { ph: 'lobby', pt: 0, round: 0, n: { P: 0, C: 0 } };
let ping = 0;
const cam = { x: 0, y: 0, z: 1 };
/** where the local player was last drawn (world px); steering is measured from here */
const drawn = { x: 0, y: 0 };
// zoom: the map's base zoom (zoomFor) times the player's choice, eased towards the target each frame. The choice lasts
// one round or story scene: every start is 1:1, and nothing is saved
const ZOOM_MIN = 0.25, ZOOM_MAX = 2.5;
const clampZoom = (v: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v));
let zoomMul = 1;
let baseZ = 1;
let W = 0, H = 0, dpr = 1;
const keys = new Set<string>();
/** the cursor over the game; on: steering by it (off until it moves on the canvas, and again after a key walks you) */
const mouse = { x: 0, y: 0, on: false };

// ------------------------------------------------------------------ transports
class Online implements Transport {
  private ws: WebSocket;
  /** `room`: a room code, or null for the open room */
  constructor(room: string | null) {
    this.ws = new WebSocket((location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws' + (room ? `?room=${room}` : ''));
    this.ws.onopen = () => this.send({ t: 'join' }); // no name, no face, no side: the server draws the side, and you pick a face at select
    this.ws.onmessage = (ev) => { try { onMsg(JSON.parse(ev.data)); } catch { /* ignore malformed */ } };
    this.ws.onclose = (ev) => {
      if (transport !== this) return;
      leave(ev.code === 4001 ? 'Nobody else came in time, so the kafila was called off. Join again to wait for another.'
        : ev.code === 4400 ? 'That room code does not look right. Check the invite link.'
        : ev.code === 1013 ? (room ? (ev.reason === 'full' ? 'This room is full: 8 is the most. Make a new room, or join the open march.' : 'No rooms free on the server. Try again in a minute.') : 'Server is full. Try again in a minute, or roam the city offline.')
        : ev.code === 1008 ? 'Disconnected (rate limit / too many connections).' : 'Disconnected from server.');
    };
  }
  send(m: Record<string, unknown>) { if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m)); }
  close() { this.ws.onclose = null; this.ws.close(); }
}

/** Same interface, but the sim runs right here in the browser (the sim is shared code). */
class Offline implements Transport {
  private sim: Sim; private id: number; private acc = 0;
  /** roam: where to put the player once the round is on (the round's start would respawn them) */
  private at: Vec | null = null;
  /** `extra`: story mode's options (its scenes are offline) */
  constructor(name: string, team: Team, extra: Partial<SimOptions> = {}, av = -1) {
    // ?debug&npcs=300 fills practice with a bigger crowd, to look at crowds
    const n = location.search.includes('debug') ? parseInt(new URLSearchParams(location.search).get('npcs') || '', 10) : NaN;
    const chalo = chaloOn && chaloData ? { chalo: chaloData, minPlayers: 1, debug: true } : {};
    this.sim = createSim(map, { countdown: 3, stages, ...chalo, ...(n >= 0 && n <= 2000 ? { npcs: n } : {}), ...extra });
    if (location.search.includes('debug')) (window as any).__kafilaSim = this.sim; // browser checks stage a scene in practice
    const e = this.sim.addPlayer(name, team, av);
    this.id = e.id;
    queueMicrotask(() => onMsg({ t: 'welcome', id: e.id, team: e.team, name: e.name, tickRate: TICK_RATE, map: map.name, mode: this.sim.geo ? 'chalo' : 'stages' }));
  }
  send(m: Record<string, unknown>) {
    if (m.t === 'in') this.sim.input(this.id, m);
    else if (m.t === 'team') this.sim.setTeam(this.id, m.team);
    else if (m.t === 'dbg' && m.a === 'open') this.sim.debugOpen(this.id);
    else if (m.t === 'act') this.sim.act(this.id, m.a, m.o);
    else if (m.t === 'dbg' && m.a === 'tp' && location.search.includes('debug')) { // screenshot scripts: stand somewhere
      const e = this.sim.S.ents.find((q) => q.id === this.id);
      if (e) { e.x = Number(m.x); e.y = Number(m.y); }
    }
    else if (m.t === 'ping') queueMicrotask(() => onMsg({ t: 'pong', c: m.c }));
  }
  pump(ms: number) {
    this.acc += ms;
    while (this.acc >= DT * 1000) {
      this.acc -= DT * 1000; this.sim.step();
      if (this.at && this.sim.S.phase === 'playing') { this.sim.place(this.id, this.at, false); this.at = null; }
      onMsg(this.sim.snapshot(this.id));
    }
  }
  /** roam: stand the player at p (world px), now or as soon as the round is on */
  place(p: Vec) { this.at = p; }
  /** story mode: time the player spends off the clock runs the Gather on (sim.spend) */
  spend(seconds: number) { this.sim.spend(seconds); }
  close() { /* nothing to release */ }
}

// ------------------------------------------------------------------ messages
function onMsg(m: any) {
  switch (m.t) {
    case 'welcome':
      meId = m.id; myTeam = m.team; playing = true; pending = []; seq = 0; snaps = []; names.clear(); chs = null;
      room.id = m.no ?? 0; room.size = m.size ?? 0; lobbyW.reset(room.id, room.size);
      welcomeMode = m.mode; chaloOn = m.mode === 'chalo' && !!geo;
      menu.classList.add('hide'); zoomBox.classList.add('on');
      // story mode loads its own maps (the doorstep, then the arrival); only a game's welcome falls back to the game map
      if (m.world === 'city' || m.world === 'station' || (!story && !roam && loadedJson !== gameJson)) void setWorld(m.world === 'city' || m.world === 'station' ? m.world : 'game', m.mapUrl);
      metroC.welcome(m);
      showInvite(typeof m.room === 'string' ? m.room : null);
      return;
    case 'pong': ping = Math.round(performance.now() - m.c); return;
    case 'ev':
      lobbyW.event(m as RoomEvent, meId);
      if (m.k === 'join' && (m.you || m.id === meId)) myDrop = typeof m.at === 'string' ? m.at : '';
      else if (m.k === 'join') chime();
      return;
    case 'mp': case 'voted': metroC.msg(m); return;
    case 'arrive': {
      metroC.msg(m);
      const a = metroC.arrivalCard();
      if (a) card.show(a.line, a.stage, a.debrief);
      return;
    }
    case 's': onSnap(m as Snapshot); return;
    // character select: the grid opens and refreshes on `sel`, and closes on `sel-done` with the face you wear
    case 'sel':
      if (typeof m.me === 'number') selNo = m.me;
      if (myPick(m.picks ?? [], selMe()).locked) { selectV.hide(); return; } // locked riders still hear their side's picks: done for you
      if (!selectV.open) { keys.clear(); mouse.on = false; releaseStick(); } // you stand while you pick
      selectV.show(m); return;
    case 'sel-done':
      selectV.hide();
      if (isAvatar(m.av)) { myAv = m.av; if (roster('P').includes(m.av)) store.set('kafila_face', String(m.av)); } // offline you march: only a protester face carries over
      return;
  }
}

function onSnap(s: Snapshot) {
  if (switching) return;
  const rows = new Map<number, number[]>();
  for (const r of s.e) rows.set(r[0], r);
  snaps.push({ rt: performance.now(), rows });
  if (snaps.length > 8) snaps.shift();
  if (s.round !== hud.round) setZoom(1); // each round starts at 1:1
  hud.ph = s.ph; hud.pt = s.pt; hud.round = s.round; hud.n = s.n;
  if (s.nm) { names.clear(); for (const [id, n] of s.nm) names.set(id, n); }
  chs = s.ch ?? null;
  if (s.sg !== undefined) { stage.sg = s.sg; stage.sv = s.sv ?? 0; stage.sgt = s.sgt ?? 0; stage.k = s.k ?? stage.k; }
  meFree = s.fr === 1;
  if (s.me) reconcile(s.me);
  audio.snap(s.ph, s.tk, chaloOn ? chs : null);
}

/** What the server's corrections still owe the drawn player (world px); it eases to nothing, so a correction never jumps */
const err = { x: 0, y: 0 };
/** corrections the server made to our prediction while walking (debug hook): how many, and the largest, in world px */
const corr = { n: 0, max: 0 };
function reconcile(me: NonNullable<Snapshot['me']>) {
  myTeam = me.team;
  meHeld = me.dt ?? 0; meSit = (me.si ?? 0) - 1;
  // a big gap means the server teleported us (spawn, team switch, the bus): snap instead of replaying. In the bus
  // nothing walks, so there is nothing to replay
  const jump = Math.hypot(P.x - me.x, P.y - me.y) > SPEED || (P.x === 0 && P.y === 0) || meHeld > 0; // more than a second of walking
  const bx = P.x, by = P.y;
  P.x = me.x; P.y = me.y; P.vx = me.vx; P.vy = me.vy; P.st = me.st ?? 1; P.jog = me.jg === 1; P.rest = me.rs ?? 0;
  pending = pending.filter((p) => p.s > me.ack);
  if (jump) { pending = []; P.px = P.x; P.py = P.y; err.x = err.y = 0; return; }
  const R = rules();
  for (const p of pending) moveHuman(map, P, p.x, p.y, p.r, R, DT); // replay inputs the server has not seen yet
  P.px += P.x - bx; P.py += P.y - by; // keep the render offset continuous
  err.x -= P.x - bx; err.y -= P.y - by; // and draw the body where it was, easing to the corrected place
  const c = Math.hypot(P.x - bx, P.y - by); if (c > 1e-3) { corr.n++; corr.max = Math.max(corr.max, c); }
}

// ------------------------------------------------------------------ input
/**
 * Steering by the cursor, no click: you walk the way it points from your feet. Two rings round you, in screen px:
 * inside the first you walk, out past it you jog (as Shift does). A small dead zone at your feet stands you still, and
 * so does the cursor out past the second ring, so reaching for a button never runs you off.
 */
function rings() { const r1 = Math.max(70, Math.min(140, Math.min(W, H) * 0.13)); return { dead: 16, r1, r2: r1 * 1.9 }; }
/** Thumb stick, in the same screen px as the cursor rings, or null when the thumb is up. */
let stick: { dx: number; dy: number; d: number } | null = null;
/** The stick's dead zone, walk ring and jog ring, in CSS px. Smaller than the cursor rings; the thumb maps onto them. */
const STICK_DEAD = 18, STICK_R1 = 56, STICK_R2 = 96;
function fromStick(dx: number, dy: number): { dx: number; dy: number; d: number } {
  const d = Math.hypot(dx, dy);
  if (d <= STICK_DEAD) return { dx: 0, dy: 0, d: 0 };
  const R = rings(), clamped = Math.min(d, STICK_R2);
  const nd = clamped <= STICK_R1
    ? R.dead + ((clamped - STICK_DEAD) / (STICK_R1 - STICK_DEAD)) * (R.r1 - R.dead)
    : R.r1 + ((clamped - STICK_R1) / (STICK_R2 - STICK_R1)) * (R.r2 - R.r1);
  return { dx: (dx / d) * nd, dy: (dy / d) * nd, d: nd };
}
/** Where the cursor is from you, or null when it is not steering. Screen px. */
function cursor(): { dx: number; dy: number; d: number } | null {
  if (!mouse.on || !playing || panDrag || mapWin?.open || mini.hit(mouse.x, mouse.y)) return null;
  // from where you are drawn, the same spot the rings are drawn round
  const dx = mouse.x - ((drawn.x - cam.x) * cam.z + W / 2), dy = mouse.y - ((drawn.y - cam.y) * cam.z + H / 2);
  return { dx, dy, d: Math.hypot(dx, dy) };
}
/** The stick while a thumb is down, otherwise the cursor. */
function steer(): { dx: number; dy: number; d: number } | null {
  if (stick && playing && !mapWin?.open) return stick;
  return cursor();
}
/** The cursor out past the outer ring: you stand still. Never the stick, which tops out on that ring. */
const pastRings = (c: { d: number } | null) => !!c && c !== stick && c.d > rings().r2;
const keyWalk = () => ['KeyA', 'KeyD', 'KeyW', 'KeyS', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].some((k) => keys.has(k));

function inputVector(): { x: number; y: number } {
  let x = 0, y = 0;
  if (keys.has('KeyA') || keys.has('ArrowLeft')) x -= 1;
  if (keys.has('KeyD') || keys.has('ArrowRight')) x += 1;
  if (keys.has('KeyW') || keys.has('ArrowUp')) y -= 1;
  if (keys.has('KeyS') || keys.has('ArrowDown')) y += 1;
  if (x || y) { const m = Math.hypot(x, y); return { x: x / m, y: y / m }; }
  const c = steer(), R = rings();
  if (!c || c.d <= R.dead || pastRings(c)) return { x: 0, y: 0 };
  // inside the first ring a walk that eases up from your feet: half pace by the dead zone, full by two thirds out
  const k = Math.min(1, 0.5 + 0.5 * (c.d - R.dead) / (R.r1 * 0.67 - R.dead));
  return { x: (c.dx / c.d) * k, y: (c.dy / c.d) * k };
}
/**
 * Jog: Shift, or the cursor between the first ring and the second (once jogging, a few px back inside still jogs, so the
 * edge never flickers). Past the second it stands you still, Shift or not.
 */
let ringJog = false;
function wantJog(): 0 | 1 {
  const c = steer();
  if (!keyWalk() && pastRings(c)) { ringJog = false; return 0; }
  if (keys.has('ShiftLeft') || keys.has('ShiftRight')) return 1;
  ringJog = !!c && !keyWalk() && c.d > rings().r1 - (ringJog ? 12 : 0);
  return ringJog ? 1 : 0;
}

function sampleInput() {
  const v = meHeld > 0 || selectV.open ? { x: 0, y: 0 } : inputVector(); // in the bus you wait; the server acks and ignores walking
  const s = ++seq, r = wantJog();
  pending.push({ s, x: v.x, y: v.y, r });
  if (pending.length > 60) pending.shift();
  P.px = P.x; P.py = P.y;
  moveHuman(map, P, v.x, v.y, r, rules(), DT);
  transport?.send({ t: 'in', s, x: v.x, y: v.y, r });
}

/** The play area that holds the local player now (protesters, while a stage is on), as the sim decides it. */
function myMask(): Uint8Array | undefined {
  if (chaloOn) return geo && chs && hud.ph === 'playing' && myTeam === 'P' ? chaloMask(map, geo, chs.g) : undefined;
  return view && hud.ph === 'playing' && stage.sg >= 0 && myTeam === 'P' && !meFree ? view.mask(stage.sv, stage.sg) : undefined;
}
/** The same movement rules the sim applies to this player, so prediction matches. */
function rules(): MoveRules {
  const metre = map.mpt ? map.tile / map.mpt : 1;
  const jog = chaloOn && chaloData && stages ? chaloData.rules.players.jogMps : stages ? stages.jog.speedMps : 0;
  return { speed: SPEED, jogSpeed: jog ? jog * metre : SPEED, jog: stages?.jog, mask: myMask() };
}

addEventListener('keydown', (e) => {
  if (selectV.open) { if (e.code === 'Escape') leave(''); else if (e.code === 'KeyN' && !e.repeat) audio.toggle(); else if (selectV.key(e.code)) e.preventDefault(); return; } // character select takes every key: nobody walks while picking
  if (story && !e.repeat && storyKey(e.code)) { e.preventDefault(); return; }
  if (story?.scene === 'ride') { if (e.code === 'Escape') leave(''); return; } // the ride takes no walking keys
  if (metroC.covering()) { if (e.code === 'Escape') leave(''); else if (e.code === 'KeyN' && !e.repeat) audio.toggle(); else if (e.code === 'KeyV' && !e.repeat && metroC.where === 'platform') setPaVoice(paVoice === 'male' ? 'female' : 'male'); else if (!e.repeat && metroC.key(e.code)) e.preventDefault(); return; } // the platform and the train: votes only
  if (picker.open) { if (e.code === 'Escape' || (roam && e.code === 'KeyM' && !e.repeat)) { e.preventDefault(); picker.close(); } return; } // roam's place picker: the list takes the keys
  if (playing && e.code === 'KeyM' && !e.repeat) { toggleMap(); return; }
  if (playing && e.code === 'KeyN' && !e.repeat) { audio.toggle(); return; }
  if (mapWin?.open) {
    if (e.code === 'Escape') { mapWin.close(); return; }
    if (mapWin.key(e.code, e.key)) { e.preventDefault(); return; }
  }
  if (phone.open && !story) { if (e.code === 'Escape') { phone.hide(); playBtn.focus(); } else if (phone.key(e.code)) e.preventDefault(); return; } // online: how you get there
  if (e.code === 'Escape' && playing) { leave(''); return; }
  if (playing && (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd')) { setZoom(zoomMul * 1.25); return; }
  if (playing && (e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract')) { setZoom(zoomMul / 1.25); return; }
  if (playing && (e.key === '0' || e.code === 'Numpad0')) { setZoom(1); return; }
  if (playing && e.code === 'KeyC') { recentre(); return; }
  // walking with the keys brings the camera back to you
  if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) { recentre(); mouse.on = false; } // and the keys take over from the cursor
  if (e.code === 'KeyG' && playing && chaloOn) { transport?.send({ t: 'dbg', a: 'open' }); return; } // opens a gate when the sim allows debug
  // Chalo's games: E sits in, talks or stands you up; 1-3 pick a line in a talk. The sim decides what they do
  if (e.code === 'KeyE' && playing && chaloOn && !e.repeat) { transport?.send({ t: 'act', a: 'use' }); return; }
  const pick = ['Digit1', 'Digit2', 'Digit3'].indexOf(e.code);
  if (pick >= 0 && playing && chaloOn && myTalk()) { transport?.send({ t: 'act', a: 'say', o: pick + 1 }); return; }
  if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) e.preventDefault();
  keys.add(e.code);
});
addEventListener('keyup', (e) => keys.delete(e.code));
addEventListener('blur', () => { keys.clear(); mouse.on = false; releaseStick(); });
canvas.addEventListener('mousedown', (e) => {
  if (story?.ride) { if (e.button === 0) story.ride.click(e.clientX, e.clientY); return; } // the ride's choices
  if (metroC.covering()) { if (e.button === 0) metroC.click(e.clientX, e.clientY); return; } // the shared ride's vote
  if (playing && e.button === 0 && mini.hit(e.clientX, e.clientY)) { toggleMap(); return; } // the minimap opens the map
  if (playing && e.button === 0 && audio.hit(e.clientX, e.clientY)) { audio.toggle(); return; } // the speaker glyph mutes
  // right or middle drag, or Space + left drag, pans; walking needs no click, the cursor steers
  if (playing && (e.button === 1 || e.button === 2 || (e.button === 0 && keys.has('Space')))) { startPan(e.clientX, e.clientY); e.preventDefault(); return; }
});
addEventListener('mouseup', () => endPan());
canvas.addEventListener('mousemove', (e) => { if (panDrag) movePan(e.clientX, e.clientY); });
// a mouse or pen steers; a finger on the map drags it (below). A tablet walks with the stick.
canvas.addEventListener('pointermove', (e) => {
  if (e.pointerType === 'touch') return;
  if (!mouse.on && Math.hypot(e.clientX - mouse.x, e.clientY - mouse.y) < 3) return; // a twitch does not take over from the keys
  mouse.x = e.clientX; mouse.y = e.clientY; mouse.on = true;
});
canvas.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') mouse.on = false; }); // off the game, or onto a button: stand

// ------------------------------------------------------------------ pan: look around the map without walking
// pan is the camera's offset from the player in world px. It stays until you recentre (C, the ◎ button) or walk.
const pan = { x: 0, y: 0 }, panAt = { x: 0, y: 0 }; // panAt: the pan the camera shows, easing to pan
let panDrag: { sx: number; sy: number; px: number; py: number } | null = null;
function startPan(sx: number, sy: number) { panDrag = { sx, sy, px: pan.x, py: pan.y }; canvas.classList.add('panning'); }
function movePan(sx: number, sy: number) {
  if (!panDrag) return;
  pan.x = panDrag.px - (sx - panDrag.sx) / cam.z; pan.y = panDrag.py - (sy - panDrag.sy) / cam.z;
}
function endPan() { panDrag = null; canvas.classList.remove('panning'); }
function recentre() { pan.x = pan.y = 0; endPan(); }
/** Keep the camera's centre on the map: px, py is the player's position. */
function clampPan(px: number, py: number) {
  pan.x = Math.max(-px, Math.min(map.width - px, pan.x));
  pan.y = Math.max(-py, Math.min(map.height - py, pan.y));
}
canvas.addEventListener('contextmenu', (e) => e.preventDefault());

// wheel and trackpad: a trackpad pinch arrives as a wheel event with ctrlKey set, in finer steps than a mouse wheel
canvas.addEventListener('wheel', (e) => {
  e.preventDefault();
  if (!playing) return;
  const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY; // lines -> px
  setZoom(zoomMul * Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0015)));
}, { passive: false });

// touch on the map: one finger drags it, two fingers pinch to zoom. Walking is the stick.
const touches = new Map<number, { x: number; y: number }>();
let pinch: { d: number; mul: number } | null = null;
const spread = () => { const [a, b] = [...touches.values()]; return Math.hypot(a.x - b.x, a.y - b.y); };
canvas.addEventListener('pointerdown', (e) => {
  if (e.pointerType !== 'touch') return;
  if (playing && mini.hit(e.clientX, e.clientY)) return; // the tap opens the map (the mousedown it becomes)
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (touches.size === 1 && playing) startPan(e.clientX, e.clientY);
  if (touches.size === 2) { endPan(); pinch = { d: spread(), mul: zoomMul }; }
});
canvas.addEventListener('pointermove', (e) => {
  if (!touches.has(e.pointerId)) return;
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pinch && touches.size === 2 && playing) setZoom(pinch.mul * spread() / (pinch.d || 1));
  else if (touches.size === 1) movePan(e.clientX, e.clientY);
});
const lift = (e: PointerEvent) => { touches.delete(e.pointerId); if (touches.size < 2) pinch = null; if (!touches.size) endPan(); };
canvas.addEventListener('pointerup', lift);
canvas.addEventListener('pointercancel', lift);

// ------------------------------------------------------------------ the walk stick (tablet): same walk and jog as the cursor rings
const touchWalk = matchMedia('(any-pointer: coarse)').matches || navigator.maxTouchPoints > 0;
if (touchWalk) document.documentElement.classList.add('touchwalk');
const stickEl = $('stick'), stickKnob = $('stick-knob'), stickCap = stickEl.querySelector('.cap') as HTMLElement;
let stickId: number | null = null, stickBack = false;
function releaseStick() {
  stickId = null; stick = null; stickBack = false;
  stickKnob.style.transform = 'translate(0px, 0px)';
  stickEl.classList.remove('jog');
  if (stickCap) stickCap.textContent = 'Walk';
}
function aimStick(e: PointerEvent) {
  const b = stickEl.getBoundingClientRect();
  const dx = e.clientX - (b.left + b.width / 2), dy = e.clientY - (b.top + b.height / 2);
  const d = Math.hypot(dx, dy) || 1, c = Math.min(d, STICK_R2), x = (dx / d) * c, y = (dy / d) * c;
  stickKnob.style.transform = `translate(${x}px, ${y}px)`;
  stick = fromStick(x, y);
  const jog = stick.d > rings().r1;
  stickEl.classList.toggle('jog', jog);
  if (stickCap) stickCap.textContent = jog ? 'Jog' : 'Walk';
  if (stick.d > rings().dead && !stickBack) { stickBack = true; recentre(); } // a walk brings the camera back, as the keys do
}
function syncStick() {
  const on = touchWalk && playing && !story?.ride && !metroC.covering() && !mapWin?.open && !selectV.open;
  stickEl.classList.toggle('on', on);
  stickEl.setAttribute('aria-hidden', on ? 'false' : 'true');
  if (!on && stickId !== null) releaseStick();
}
stickEl.addEventListener('pointerdown', (e) => {
  if (!stickEl.classList.contains('on') || stickId !== null) return;
  stickId = e.pointerId; stickEl.setPointerCapture(e.pointerId); mouse.on = false;
  aimStick(e); e.preventDefault();
});
stickEl.addEventListener('pointermove', (e) => { if (e.pointerId !== stickId) return; aimStick(e); });
const stickUp = (e: PointerEvent) => { if (e.pointerId === stickId) releaseStick(); };
stickEl.addEventListener('pointerup', stickUp);
stickEl.addEventListener('pointercancel', stickUp);

const zoomBox = $('zoom'), zoomReset = $('zr');
setZoom(1); // label the reset button
$('zi').onclick = () => setZoom(zoomMul * 1.25);
$('zo').onclick = () => setZoom(zoomMul / 1.25);
zoomReset.onclick = () => setZoom(1);
$('zc').onclick = recentre;
$('zm').onclick = () => toggleMap();

// ------------------------------------------------------------------ the map window and the pin
function toggleMap() {
  mouse.on = false; keys.clear(); releaseStick(); // stop walking while the map is up
  if (roam) { picker.show({ here: roam.at, onPick: roamTo, onClose: () => {} }); return; } // roam: the map picks where you are
  mapWin.toggle();
}
function setPin(p: Vec | null) { pin = p; mapWin.pin = p; }
/** What the minimap and the map window show this frame. */
function mapLive(mx: number, my: number): MapLive {
  const v = inputVector(), staged = view && hud.ph === 'playing' && stage.sg >= 0;
  const others: MapLive['others'] = [];
  const lastSnap = snaps[snaps.length - 1];
  if (lastSnap) for (const r of lastSnap.rows.values()) if (r[0] !== meId) others.push({ x: r[2], y: r[3], type: r[1], name: names.get(r[0]), crowd: r[1] === 2 && r[5] === 1 });
  const z = cam.z;
  return {
    me: { x: mx, y: my, face: v.x || v.y ? Math.atan2(v.y, v.x) : Math.hypot(P.vx, P.vy) > 1 ? Math.atan2(P.vy, P.vx) : -Math.PI / 2 },
    team: myTeam === 'C' ? 1 : 0, others,
    cam: { x0: cam.x - W / 2 / z, y0: cam.y - H / 2 / z, x1: cam.x + W / 2 / z, y1: cam.y + H / 2 / z },
    dim: staged && myTeam === 'P' ? view!.dim(stage.sv, stage.sg) : null,
    zone: staged ? (zoneAt ??= view!.zoneCentre()) : null,
    chalo: chaloOn && geo ? { geo, ch: chs, dark: dark()?.hides.includes('minimap') ? dark()!.left : 0 } : null,
    pin,
  };
}
/** The pin on the street (world coordinates): a flag at its foot, and how far. */
function drawPinWorld(z: number, mx: number, my: number) {
  if (!pin) return;
  const metre = map.mpt ? map.tile / map.mpt : 15;
  if (Math.hypot(pin.x - mx, pin.y - my) < 4 * metre) { setPin(null); return; } // reached
  ctx.save(); ctx.translate(pin.x, pin.y); ctx.scale(1 / z, 1 / z); drawPinMark(ctx, 0, 0, 34); ctx.restore();
}
/** Off screen, an arrow at the edge points at the pin, with the distance. Screen px. */
function drawPinArrow(mx: number, my: number) {
  if (!pin) return;
  const z = cam.z, sx = (pin.x - cam.x) * z + W / 2, sy = (pin.y - cam.y) * z + H / 2, pad = 40;
  const d = Math.round(Math.hypot(pin.x - mx, pin.y - my) / (map.mpt ? map.tile / map.mpt : 15));
  if (sx > pad && sx < W - pad && sy > pad && sy < H - pad) { tag(ctx, `${d} M`, sx, sy + 8, { font: FONT.mono(12, 600), fill: INK.metro, align: 'center', h: 20, shadow: 2 }); return; }
  // the arrow runs round an inset frame that keeps clear of the top bar and the minimap's corner
  const top = 90, cx = W / 2, cy = (top + H - pad) / 2, ang = Math.atan2(sy - cy, sx - cx);
  const k = Math.min((W / 2 - pad) / Math.abs(Math.cos(ang) || 1e-6), ((H - pad - top) / 2) / Math.abs(Math.sin(ang) || 1e-6));
  const ax = cx + Math.cos(ang) * k, ay = cy + Math.sin(ang) * k;
  ctx.save(); ctx.translate(ax, ay); ctx.rotate(ang);
  ctx.beginPath(); ctx.moveTo(14, 0); ctx.lineTo(-6, 9); ctx.lineTo(-2, 0); ctx.lineTo(-6, -9); ctx.closePath();
  ctx.fillStyle = INK.metro; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = INK.asphalt; ctx.stroke(); ctx.restore();
  const ty = ay + (Math.sin(ang) < -0.5 ? 14 : -30);
  tag(ctx, `PIN ${d} M`, ax - Math.cos(ang) * 18, ty, { font: FONT.mono(12, 600), fill: INK.metro, align: 'center', h: 20, shadow: 2 });
}

// ------------------------------------------------------------------ rendering
function resize() {
  dpr = Math.min(2, devicePixelRatio || 1);
  W = innerWidth; H = innerHeight;
  canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
  canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
  baseZ = zoomFor(map);
  cam.z = baseZ * zoomMul;
}

function setZoom(mul: number) {
  zoomMul = clampZoom(mul);
  zoomReset.textContent = Math.abs(zoomMul - 1) < 0.01 ? '1:1' : `${Math.round(zoomMul * 100)}%`;
}
addEventListener('resize', resize);

/** Metre-scale maps get a fixed zoom (12 screen px per metre: 2.25 on 7.5 m tiles); legacy maps fit the window. */
const SCREEN_PX_PER_METRE = 8;
function zoomFor(m: GameMap | undefined): number {
  if (m?.mpt) return Math.max(1, Math.min(3, (SCREEN_PX_PER_METRE * m.mpt) / m.tile));
  return Math.max(0.45, Math.min(1.6, Math.min(W / 1100, H / 760)));
}

const INTERP_MS = 100; // render other entities this far in the past so there are always two snapshots to blend

function entsNow(): DrawEnt[] {
  if (!snaps.length) return [];
  const rt = performance.now() - INTERP_MS;
  let a = snaps[0], b = snaps[0];
  for (let i = 0; i < snaps.length; i++) {
    if (snaps[i].rt <= rt) { a = snaps[i]; b = snaps[Math.min(i + 1, snaps.length - 1)]; }
  }
  const k = b.rt > a.rt ? Math.max(0, Math.min(1, (rt - a.rt) / (b.rt - a.rt))) : 1;
  const out: DrawEnt[] = [];
  for (const [id, rb] of b.rows) {
    if (id === meId) continue;
    const ra = a.rows.get(id) || rb, x = ra[2] + (rb[2] - ra[2]) * k, y = ra[3] + (rb[3] - ra[3]) * k;
    // a toli: its people, standing closer or looser by its hausla
    if (rb[1] === 3) out.push(...toliBodies(id, x, y, rb[4] / 100, rb[5] ?? 5, map.mpt ? map.tile / map.mpt : 15, rb[6] ?? 100, stages?.separation.personalSpaceM ?? 0));
    else out.push({ id, type: rb[1], x, y, face: rb[4] / 100, ...(rb[1] === 4 || rb[1] === 2 ? { k: rb[5] } : {}) }); // a friend's place, an npc's kafila bit
  }
  return out;
}

/** The phase's announcement: a stamp, built as the logo is (src/client/ui.ts), its top at bannerY(). */
const bannerY = () => Math.round(H * 0.22 - 44);
function banner(text: string, sub = '') { stamp(ctx, text, sub, W / 2, bannerY(), W < 600 ? 32 : 46); }

/** The clock: metro-yellow figures on an asphalt block at the top, with what to do now on a strip pasted under it. */
function hudClock(t: string, line = '') {
  ctx.font = FONT.display(38);
  const tw = Math.max(92, ctx.measureText(t).width + 30);
  box(ctx, W / 2 - tw / 2, 10, tw, 44, INK.asphalt, 0, 4);
  ctx.fillStyle = INK.metro; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(t, W / 2, 35);
  if (line) tag(ctx, line, W / 2, 54, { font: FONT.body(13, 700), fill: INK.metro, align: 'center', h: 26, shadow: 3 });
  ctx.textBaseline = 'alphabetic';
}

function draw(alpha: number, dt: number) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = INK.asphalt; ctx.fillRect(0, 0, W, H);
  if (!playing) return;

  const ek = Math.exp(-dt / 0.1); err.x *= ek; err.y *= ek; // a correction eases out over about a tenth of a second
  const mx = P.px + (P.x - P.px) * alpha + err.x, my = P.py + (P.y - P.py) * alpha + err.y; // smoothed local position
  drawn.x = mx; drawn.y = my;
  clampPan(mx, my);
  // the camera stays on you, so you never slide off the centre (and away from the steering rings) as you speed up;
  // only the pan eases, and follows the finger or mouse exactly while dragging
  const k = panDrag ? 1 : 1 - Math.exp(-dt * 12);
  panAt.x += (pan.x - panAt.x) * k; panAt.y += (pan.y - panAt.y) * k;
  cam.x = mx + panAt.x; cam.y = my + panAt.y;
  frameMs += (dt * 1000 - frameMs) * 0.05;
  if (stages && map.mpt) {
    // zoom with movement: wider while walking so you see where you are going, closer when you stop
    const Z = stages.zoom, moving = Math.hypot(P.vx, P.vy) / (map.tile / map.mpt) > Z.movingMps;
    const tz = ((moving ? Z.walkPxPerMetre : Z.standPxPerMetre) * map.mpt / map.tile) * zoomMul;
    cam.z = Math.exp(Math.log(cam.z) + (Math.log(tz) - Math.log(cam.z)) * (1 - Math.exp(-dt / (Z.easeS / 3))));
  } else {
    const tz = baseZ * zoomMul;
    cam.z = Math.abs(tz - cam.z) < 1e-3 * tz ? tz : cam.z * Math.pow(tz / cam.z, 0.25); // eased in log space
  }
  const z = cam.z, vw = W / z, vh = H / z, vx0 = cam.x - vw / 2, vy0 = cam.y - vh / 2;

  // world layer: only the chunks in view, over the dimmed streets past the edge
  surround?.draw(ctx, vx0, vy0, vw, vh, z);
  layer.draw(ctx, vx0, vy0, vw, vh, z);

  ctx.save(); ctx.scale(z, z); ctx.translate(-vx0, -vy0);
  drawBorder(ctx, map, z);
  surround?.drawEdge(ctx, z);
  drawPois(ctx, map, vx0, vy0, vw, vh, z);
  const ents = entsNow();
  if (traffic && chaloOn && geo) traffic.close(map, hud.ph === 'playing' ? (ringRoads ??= geo.level.map((l, i) => (l > 0 || geo!.cordon[i] || geo!.gapOf[i] ? 1 : 0))) : null, stages?.traffic?.fadeOutS ?? 2); // the cordon is the line
  else if (traffic && stages?.traffic) { // the march's roads are closed to cars from the stage that opens them
    const T = stages.traffic, open = hud.ph === 'playing' && stage.sg >= 0 && T.closeRoadsInStageArea.includes((['gather', 'march', 'stand'] as const)[stage.sg]);
    traffic.close(map, open && view ? view.mask(stage.sv, stage.sg) : null, T.fadeOutS);
  }
  if (traffic) { // on the ground, under the people; stops for everyone, you included
    traffic.update(dt, vx0, vy0, vw, vh, [...ents, { x: mx, y: my }]);
    traffic.draw(ctx, vx0, vy0, vw, vh, z);
  }
  if (convoy && chaloOn) { // the van drives out through the rings once the round is won (the march, when it is built)
    if (chs?.w === 1) convoy.vanLeaves(); else if (hud.ph === 'lobby' || hud.ph === 'countdown') convoy.reset();
    convoy.update(dt); convoy.draw(ctx, vx0, vy0, vw, vh);
    if (world) { world.hide.van = convoy.vanGone; world.hide.bus = convoy.busGone; } // the parked one goes when it drives
  }
  const staged = view && hud.ph === 'playing' && stage.sg >= 0;
  if (chaloOn && geo) {
    drawCordon(ctx, map, geo, chs, vx0, vy0, vw, vh);
    if (hud.ph === 'playing') {
      // the bus, where a detained player waits out the timer; mats under everyone sitting in
      drawMarker(ctx, geo.bus, 'Police bus', z, '#A68B5B');
      const rows = snaps.length ? [...snaps[snaps.length - 1].rows.values()] : [];
      const mats = rows.filter((r) => r[1] === 0 && r[5] === 1 && r[0] !== meId).map((r) => ({ x: r[2], y: r[3] }));
      if (meSit >= 0) mats.push({ x: mx, y: my });
      drawMats(ctx, mats, PLAYER_R);
    }
  }
  // on the ground, under the buildings and people: the land outside the area dimmed (cars too), its police lines, chevrons
  if (staged) view!.drawBounds(ctx, stage.sv, stage.sg, myTeam === 'P', vx0, vy0, vw, vh);
  if (staged && stage.sg >= 1 && myTeam === 'P') view!.drawChevrons(ctx, stage.sv, mx, my, 40, performance.now() / 1000);
  const list = ents.filter((e) => e.x > vx0 - 40 && e.x < vx0 + vw + 40 && e.y > vy0 - 40 && e.y < vy0 + vh + 40);
  list.sort((a, b) => a.y - b.y);
  const v = inputVector();
  const me: DrawEnt = { id: meId, type: myTeam === 'C' ? 1 : 0, x: mx, y: my, face: v.x || v.y ? Math.atan2(v.y, v.x) : Math.atan2(P.vy, P.vx), poster: story?.js.choices.poster ?? null };
  // people sort in with the trees and buildings by their feet, so a wall never covers someone standing in front of it
  const bodies = people.standing([...list, me], meId, dt, z, (e) => (e.type === 2 || e.type === 4 ? NPC_R : PLAYER_R));
  // the Chalo world (stage, gates, van) and the street barricades, plus each game's props while it runs
  const props = chaloOn && geo ? [...(world ? world.frame(chs, dt, vx0, vy0, vw, vh) : []), ...gameProps(geo, chs, map.mpt ? map.tile / map.mpt : 15)] : [];
  if (chaloOn && geo && chaloData && cordonProps?.geo !== geo) cordonProps = { geo, props: new CordonProps(map, geo, chaloData) };
  const barricades = chaloOn && geo && cordonProps ? cordonProps.props.standing(vx0, vy0, vw, vh) : [];
  trees.draw(ctx, vx0, vy0, vw, vh, mx, my, [...(blocks ? blocks.visible(vx0, vy0, vw, vh, mx, my, z) : []), ...(dressing ? dressing.visible(vx0, vy0, vw, vh) : []), ...bodies, ...props, ...barricades, ...(compounds ? compounds.standing(vx0, vy0, vw, vh) : [])]);
  dressing?.drawSmoke(ctx, performance.now() / 1000, vx0, vw, vy0, vh);
  blocks?.labels(ctx, vx0, vy0, vw, vh, z, mx, my);
  if (chaloOn && geo && chaloData) { // emotes over the tolis, dust where police lines slide, the van's ring
    cues ??= new CrowdCues(map, geo, chaloData.rules);
    if (snaps.length) cues.update(snaps[snaps.length - 1].rows, chs, performance.now() / 1000);
    cues.draw(ctx, list, z, performance.now() / 1000, (e) => people.heightOf(e));
  }
  const seen = list.filter((e) => e.x > vx0 && e.x < vx0 + vw && e.y > vy0 && e.y < vy0 + vh);
  onScreen = seen.length; castSeen = [...new Set([...seen, me].map((e) => `${e.type}:${castOf(e)}`))];
  if (staged && myTeam === 'P') drawLineHint(mx, my, v, dt, z);
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.font = FONT.body(Math.round(12 / z), 700); // names stay 12 screen px at any zoom: newsprint type, inked round
  for (const e of list) { const n = names.get(e.id); if (n) inked(ctx, n, e.x, e.y - people.heightOf(e) - 4 / z, INK.newsprint, 3.5 / z); }
  drawPinWorld(z, mx, my);
  if (story && story.scene !== 'ride') storyWorld(z, mx, my);
  if (metroC.where === 'station' && !switching) metroWorld(z);
  ctx.restore();

  drawRings(mx, my);
  drawStamina(mx, my);
  drawPinArrow(mx, my);
  const live = mapLive(mx, my);
  audio.frame({ x: mx, y: my }, cam, W);
  drawHud(live);
  mapWin.draw(live);
}

/**
 * Stamina under your feet: nothing while there is enough to start a jog, and below that only the words Ghee khatam,
 * no bar (gheeUnderFeet). Any phase, since you can jog in the lobby too. Screen px.
 */
function drawStamina(mx: number, my: number) {
  const words = stages ? gheeUnderFeet(P.st, stages.jog.minToStart) : null;
  if (!words) return;
  const x = Math.round((mx - cam.x) * cam.z + W / 2), y = Math.round((my - cam.y) * cam.z + H / 2 + 16);
  ctx.save();
  tag(ctx, words, x, y, { font: FONT.mono(10, 600), fill: INK.newsprint2, align: 'center', h: 16, padX: 5, bw: 1.5, shadow: 2 });
  ctx.restore();
}

/** Height of the HUD's Ghee meter, and its gap above the minimap. Screen px. */
const GHEE_H = 22, GHEE_GAP = 6;
/**
 * The Ghee meter: the stamina level, always on screen while playing with jog rules, with the jog's starting line marked.
 * It sits on the minimap's left edge, just above it: below the tablet stick (bottom 248 px up) and far from the
 * checklist (top left). Grey below the line, when a jog can't start. Screen px; call after the minimap is drawn.
 */
function drawGheeMeter() {
  if (!stages || !playing) return;
  const J = stages.jog, r = mini.rect, low = !P.jog && P.st < J.minToStart, y = r.y - GHEE_GAP - GHEE_H;
  ctx.save();
  const t = tag(ctx, name('Stamina meter'), r.x, y, { font: FONT.mono(10, 600), h: GHEE_H, padX: 6, bw: 1.5, shadow: 2 });
  const bx = t.x + t.w + 6, bw = Math.max(40, r.x + r.w - bx), bh = 10, by = y + (GHEE_H - bh) / 2;
  meter(ctx, bx, by, bw, bh, P.st, low ? INK.newsprintDim : INK.metro);
  ctx.fillStyle = INK.riso; ctx.fillRect(Math.round(bx + bw * J.minToStart) - 1, Math.round(by) - 3, 2, bh + 6); // the jog's starting line
  ctx.restore();
}

/**
 * The two steering rings round your feet while the cursor steers; the one it is in shows. Past the outer ring both fade
 * and a stop bar stands across the outer ring where the cursor left it: you are standing still. Screen px.
 */
function drawRings(mx: number, my: number) {
  if (stick) return; // the stick draws its own rings
  const c = cursor();
  if (!c || keyWalk()) return;
  const R = rings(), x = (mx - cam.x) * cam.z + W / 2, y = (my - cam.y) * cam.z + H / 2, jog = wantJog() === 1, past = pastRings(c), still = c.d <= R.dead || past;
  ctx.save(); ctx.lineWidth = 1.5;
  ctx.setLineDash([]); ctx.strokeStyle = `rgba(255,255,255,${!still && !jog ? 0.55 : past ? 0.12 : 0.22})`;
  ctx.beginPath(); ctx.arc(x, y, R.r1, 0, Math.PI * 2); ctx.stroke();
  ctx.setLineDash([6, 6]); ctx.strokeStyle = jog ? 'rgba(248,196,10,.8)' : past ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.18)';
  ctx.beginPath(); ctx.arc(x, y, R.r2, 0, Math.PI * 2); ctx.stroke();
  if (past) { // the stop bar: across the outer ring, square to the way the cursor went
    const ux = c.dx / c.d, uy = c.dy / c.d, bx = x + ux * R.r2, by = y + uy * R.r2;
    ctx.setLineDash([]); ctx.lineWidth = 3; ctx.lineCap = 'butt'; ctx.strokeStyle = INK.newsprint;
    ctx.beginPath(); ctx.moveTo(bx - uy * 10, by + ux * 10); ctx.lineTo(bx + uy * 10, by - ux * 10); ctx.stroke();
  } else if (!still) { // a short tick on the ring the way you are going
    const ux = c.dx / c.d, uy = c.dy / c.d, r = jog ? R.r2 : R.r1;
    ctx.setLineDash([]); ctx.lineWidth = 3; ctx.lineCap = 'butt'; ctx.strokeStyle = jog ? INK.metro : INK.newsprint;
    ctx.beginPath(); ctx.moveTo(x + ux * (r - 8), y + uy * (r - 8)); ctx.lineTo(x + ux * (r + 8), y + uy * (r + 8)); ctx.stroke();
  }
  ctx.restore();
}

/** After pushing against the police line for a moment, say so and point the way. World coordinates. */
function drawLineHint(mx: number, my: number, v: { x: number; y: number }, dt: number, z: number) {
  const k = myMask();
  if (!k || !stages || !view) return;
  const t = map.tile, ahead = P.r + t * 0.3;
  const tx = Math.floor((P.x + v.x * ahead) / t), ty = Math.floor((P.y + v.y * ahead) / t), i = ty * map.w + tx;
  const atLine = (v.x !== 0 || v.y !== 0) && tx >= 0 && ty >= 0 && tx < map.w && ty < map.h && !map.solid[i] && !k[i];
  // velocity is not cut by collision, so judge by how far the body really got
  const got = dt > 0 ? Math.hypot(mx - hintAt.x, my - hintAt.y) / dt : SPEED; hintAt.x = mx; hintAt.y = my;
  pushT = atLine && got < SPEED * 0.6 ? pushT + dt : 0;
  if (pushT < stages.bounds.hintAfterS) return;
  const way = stage.sg >= 1 ? view.way(stage.sv, mx, my) : null, u = view.metre;
  const msg = way ? 'Police line. The march goes this way' : 'Police line. The march sets off once the kafila gathers';
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'; ctx.font = FONT.body(Math.round(14 / z), 700);
  inked(ctx, msg, mx, my - people.heightOf({ id: meId, type: 0 }) - 20 / z, INK.metro, 4 / z);
  if (way) {
    const ax = mx + way.x * 1.5 * u, ay = my + way.y * 1.5 * u, bx = mx + way.x * 5 * u, by = my + way.y * 5 * u, ang = Math.atan2(way.y, way.x);
    ctx.strokeStyle = INK.metro; ctx.lineWidth = 0.5 * u; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
    ctx.moveTo(bx - Math.cos(ang - 0.6) * 1.5 * u, by - Math.sin(ang - 0.6) * 1.5 * u); ctx.lineTo(bx, by);
    ctx.lineTo(bx - Math.cos(ang + 0.6) * 1.5 * u, by - Math.sin(ang + 0.6) * 1.5 * u); ctx.stroke();
  }
}

/** The stage's HUD line: where you are in the arc, and what you are walking towards. */
function stageLine(): string {
  const S = stages!, k = stage.k[0], zone = S.zoneName || 'the zone';
  if (stage.sg === 0) return `GATHER · ${S.starts[stage.sv]?.name ?? ''} · kafila ${k}/${S.gather.crowd}`;
  if (stage.sg === 1) {
    const d = myTeam === 'P' ? view!.distanceM(stage.sv, P.x, P.y) : null;
    return `MARCH · ${zone}` + (d !== null && d > 0 ? ` · ${Math.round(d)} m` : '') + ` · kafila ${k}`;
  }
  return `STAND · ${zone} · kafila ${k}`;
}

/** Chalo: this player's talk with the inspector, if one is under way (a snapshot TalkRow) */
const myTalk = () => chs?.tk?.find((t) => t[1] === meId) ?? null;

/** Where the lobby dropped you and how far the site is (the map's plaza is Jantar Mantar on both the city and the game map). */
function dropLine(): string {
  if (!myDrop || switching) return 'Walk the city from the metro while people join';
  const km = Math.hypot(P.x - map.plaza.x, P.y - map.plaza.y) / (map.mpt ? map.tile / map.mpt : 1) / 1000;
  return `Dropped at ${myDrop} · ${km < 1 ? Math.round(km * 1000) + ' m' : km.toFixed(1) + ' km'} to Jantar Mantar · walk the city while people join`;
}

/**
 * The platform's own announcements while you wait, in Hindi, in the voice the rider picked (male or female, kept on
 * this device). The recording is cut into PA_PARTS lines (public/audio/pa-platform-<voice>-<n>.ogg, 4-14 s each, the
 * station-PA filter baked in: the voice band and a hall echo), said one at a time in order with a pause between, at
 * PA_VOLUME so it sounds like the far end of the platform. Lowest priority, so any other line takes the air; stopped
 * the moment you leave (only if it is still ours). Muted with the rest of the sound.
 */
type PaVoice = 'male' | 'female';
const PA_PARTS = 8, PA_VOLUME = 0.3, PA_GAP_S = [6, 11], PA_FIRST_S = 7; // the first line waits out the gate's alert
const paId = (v: PaVoice, n: number) => `pa-platform-${v}-${n}`;
const paOurs = () => { for (const v of ['male', 'female'] as const) for (let n = 1; n <= PA_PARTS; n++) if (announcer.playing(paId(v, n))) return true; return false; };
let paVoice: PaVoice = store.get('dharna_pa_voice', 'male') === 'female' ? 'female' : 'male', paWaitIn = PA_FIRST_S, paNext = 1;
let lastWhere: string | null = null, gateAlerts = 0; // gateAlerts: for ?debug, the times the gate's alert sounded
const paVoiceEl = $('pavoice');
function platformPA(dt: number, on: boolean) {
  if (paVoiceEl.hidden === on) paVoiceEl.hidden = !on; // the voice switch shows on the platform only
  if (!on) { if (paOurs()) announcer.stop(); paWaitIn = PA_FIRST_S; paNext = 1; return; }
  if (announcer.busy || (paWaitIn -= dt) > 0) return;
  if (announcer.say(paId(paVoice, paNext), 0, false, PA_VOLUME)) paNext = (paNext % PA_PARTS) + 1;
  paWaitIn = PA_GAP_S[0] + Math.random() * (PA_GAP_S[1] - PA_GAP_S[0]); // counted once the line has ended (busy holds it)
}
/**
 * The other platform, heard while you wait: a train comes in (metro-train-in), stands a few seconds, pulls out
 * (metro-train-out), and the next comes a while later. None comes in the last stretch before your own train, so the
 * two never blur. Off the platform the cycle starts again from a quiet gap.
 */
const TRAIN_GAP_S = [12, 30], TRAIN_DWELL_S = [9, 14], TRAIN_CLEAR_S = 15;
let otherTrain: { next: 'in' | 'out'; t: number } = { next: 'in', t: 6 };
function platformTrains(dt: number, on: boolean) {
  if (!on) { otherTrain = { next: 'in', t: 6 }; return; }
  if ((otherTrain.t -= dt) > 0) return;
  const between = (r: number[]) => r[0] + Math.random() * (r[1] - r[0]), mine = metroC.doorsIn();
  if (otherTrain.next === 'in') {
    if (mine !== null && mine < TRAIN_CLEAR_S) { otherTrain.t = 4; return; }
    audio.play('trainIn'); otherTrain = { next: 'out', t: between(TRAIN_DWELL_S) };
  } else { audio.play('trainOut'); otherTrain = { next: 'in', t: between(TRAIN_GAP_S) }; }
}
/** Pick the announcer's voice: kept, shown on the switch, and if the other voice is mid-announcement, it hands over. */
function setPaVoice(v: PaVoice) {
  paVoice = v; store.set('dharna_pa_voice', v);
  paVoiceEl.querySelectorAll<HTMLButtonElement>('button[data-v]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.v === v)));
  // the other voice mid-line: it stops, and the same line is said again in this one
  const other = v === 'male' ? 'female' : 'male', at = Array.from({ length: PA_PARTS }, (_, i) => i + 1).find((n) => announcer.playing(paId(other, n)));
  if (at) { announcer.stop(); paNext = at; paWaitIn = 0.3; }
}
paVoiceEl.querySelectorAll<HTMLButtonElement>('button[data-v]').forEach((b) => (b.onclick = () => setPaVoice(b.dataset.v === 'female' ? 'female' : 'male')));
setPaVoice(paVoice);

/**
 * On the platform the street is gone but where you are is not: the station's minimap, bottom-left as on the street,
 * with you at the gate you came down, and the station named over it where the Ghee meter sits. Smaller on a phone,
 * where the notice board's card sits on the floor above it.
 */
function drawPlatformMap() {
  mini.draw(ctx, 14, H - 14, W < 520 ? 130 : map.w > 200 ? 200 : 170, dpr, mapLive(P.x, P.y));
  const r = mini.rect;
  tag(ctx, `YOU ARE HERE · ${(metroC.stn?.name ?? 'the station').toUpperCase()}`, r.x, r.y - GHEE_GAP - GHEE_H, { font: FONT.mono(10, 600), h: GHEE_H, padX: 6, bw: 1.5, shadow: 2 });
}

function drawHud(live: MapLive) {
  const staged = view && hud.ph === 'playing' && stage.sg >= 0;
  mini.draw(ctx, 14, H - 14, map.w > 200 ? 200 : 170, dpr, live);
  drawGheeMeter();

  // top bar: two docket tags, the count and side on the left, the keys on the right
  const line = story ? storyTopLine() : roam ? `ROAMING · ${roam.at.name.toUpperCase()}` : `✊ ${hud.n.P}   👮 ${hud.n.C}   ·   ${myTeam === 'P' ? 'PROTESTERS' : 'POLICE'}${ping ? '   ·   ' + ping + ' MS' : ''}`;
  const left = tag(ctx, line, 12, 10, { font: FONT.mono(12, 600), h: 26 });
  if (!story && !roam) { ctx.fillStyle = myTeam === 'P' ? INK.metro : INK.barricade; ctx.fillRect(left.x, left.y, 6, left.h); }
  const hint = touchWalk
    ? 'Stick walks · outer ring jogs · drag to look'
    : story ? (story.scene === 'doorstep' ? 'Cursor steers · Shift jog · M map · N sound · Esc menu' : 'Cursor steers · outer ring/Shift jog · E use · M map · N sound · Esc menu') : roam ? 'Cursor steers · outer ring/Shift jog · M pick a place · N sound · Esc menu' : chaloOn ? 'Cursor steers · outer ring/Shift jog · E sit in / talk · 1-3 pick a line · M map · N sound · Esc menu' : stages ? 'Cursor steers · outer ring/Shift jog · M map · N sound · Esc menu' : 'Cursor steers · M map · N sound · Esc menu';
  ctx.font = FONT.mono(11, 500);
  if (ctx.measureText(hint).width + 16 < W / 2 - 70) tag(ctx, hint, W - 12, 10, { font: FONT.mono(11, 500), fill: INK.asphalt, ink: INK.newsprint, align: 'right', h: 26, shadow: 0 });
  if (map.attribution) { // ODbL: the credit is shown whenever the map is
    ctx.font = FONT.mono(10, 500); const aw = ctx.measureText(map.attribution).width;
    ctx.fillStyle = 'rgba(236,224,200,.85)'; ctx.fillRect(W - 76 - aw - 10, H - 22, aw + 12, 16);
    ctx.fillStyle = INK.asphalt; ctx.textAlign = 'right'; ctx.textBaseline = 'middle'; ctx.fillText(map.attribution, W - 76 - 4, H - 14); ctx.textBaseline = 'alphabetic';
  }

  const mmss = `${Math.floor(hud.pt / 60)}:${String(hud.pt % 60).padStart(2, '0')}`;
  if (story?.scene === 'doorstep') storyHud();
  else if (roam) { /* roam: no clock, no phase banners, no score */ }
  else if (selectV.open) { /* character select is up: its card is the banner */ }
  else if (story && hud.ph !== 'playing' && hud.ph !== 'roundOver') { /* story mode has no lobby: the round starts at once */ }
  else if (chaloOn && hud.ph === 'roundOver' && chs) { banner(...resultLine(chs)); box(ctx, W / 2 - 36, bannerY() - 82, 72, 72, INK.metro, 3, 4); drawIcon(ctx, resultIcon(chs), W / 2 - 26, bannerY() - 72, 52, INK.asphalt); }
  else if (chaloOn && hud.ph === 'lobby') banner('WAITING FOR THE TEAM', `Chalo needs ${chaloData!.rules.players.min} to ${chaloData!.rules.players.max} players`);
  else if (chaloOn && hud.ph === 'countdown') banner(`THE RINGS GO UP IN ${Math.max(1, hud.pt)}`, 'Open the checkpoints. Get 400 people to Jantar Mantar');
  else if (metroC.where === 'station' && hud.ph === 'lobby') banner(...metroC.stationBanner(metroGate.path.length ? pathLength(metroGate.path) / metre() : null));
  else if (hud.ph === 'lobby' && room.id) banner(`FINDING YOUR KAFILA · ${hud.n.P + hud.n.C}/${room.size}`, dropLine());
  else if (hud.ph === 'countdown' && room.id) banner(`ROOM FULL · THE MARCH STARTS IN ${Math.max(1, hud.pt)}`, 'Everyone moves to their start at the whistle');
  else if (hud.ph === 'lobby') banner('WAITING FOR THE KAFILA', 'Walk around while people arrive');
  else if (hud.ph === 'countdown') banner(`MARCH STARTS IN ${Math.max(1, hud.pt)}`, 'Everyone returns to their start at the whistle');
  else if (hud.ph === 'roundOver') banner('DUSK', `Next run in ${hud.pt}s`);
  else {
    hudClock(mmss, staged ? stageLine() : '');
    if (chaloOn && geo && chs) {
      // Chalo's line sits low in the middle: the checklist has the top left. Detention is a timer in the bus, never
      // shown as force (red lines)
      const t = meHeld > 0 ? null : myTalk();
      if (t) drawTalk(ctx, chaloData!, t, W, H);
      const l = meHeld > 0 ? `${name('Detained (the timer)')} · free bus ride to the thana · back in ${meHeld} s, outside the ring`
        : usePrompt(chaloData!, geo, chs, P.x, P.y, metre(), geo.level[Math.floor(P.y / map.tile) * map.w + Math.floor(P.x / map.tile)], meSit, !!t);
      if (l) tag(ctx, l, W / 2, H - 92, { font: FONT.body(meHeld > 0 ? 18 : 15, 700), fill: meHeld > 0 ? INK.asphalt : INK.metro, ink: meHeld > 0 ? INK.newsprint : INK.asphalt, align: 'center', h: meHeld > 0 ? 38 : 32, shadow: 4 });
    }
    if (story && nearStall()) {
      const l = `E · Posters (${story.B!.shop.costMin} min): carry one, or walk past`;
      tag(ctx, l, W / 2, 88, { font: FONT.body(14, 700), fill: INK.newsprint, align: 'center', h: 28, shadow: 3 });
    }
  }
  if (chaloOn && geo && chs && hud.ph === 'playing') drawChecklist(ctx, chaloLines(), 24, 82); // under the clock
  const mst = metroC.where === 'station' ? metroC.st : null; // at a station, the room's roster comes with the metro's status
  lobbyW.update(playing && room.id > 0 && (hud.ph === 'lobby' || hud.ph === 'countdown'), hud.ph, mst ? mst.names : [...names.values()], hud.pt, mst ? mst.you : [...names.keys()].indexOf(meId));
  audio.drawHud(ctx, W, H);
}

// ------------------------------------------------------------------ main loop
let last = performance.now(), acc = 0, pingT = 0;
function frame(t: number) {
  syncStick();
  const dt = Math.min(0.1, (t - last) / 1000); last = t;
  if (story) storyUpdate(dt);
  if (story?.ride) { // the ride view replaces the world while it runs
    story.ride.update(dt);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); story.ride.draw(ctx, W, H); drawFade();
    requestAnimationFrame(frame);
    return;
  }
  card.update(dt);
  // through the gate: the street gives way to the platform, and the gate's entry alert sounds once
  const whereNow = playing ? metroC.where : null;
  if (whereNow === 'platform' && lastWhere === 'station') { audio.play('gateEntry'); gateAlerts++; }
  lastWhere = whereNow;
  platformPA(dt, playing && metroC.where === 'platform'); // the platform's Hindi announcements, on a loop while you wait
  platformTrains(dt, playing && metroC.where === 'platform'); // and trains coming and going on the other platform
  audio.platformBed(playing && metroC.where === 'platform'); // and the station's air with a soft track under it
  selectV.tick(); // character select's seconds
  feed.update(waitingForRoom()); // the doomscroll docks while your room fills, and goes when it is full
  audio.titleBed(!menu.classList.contains('hide')); // the title's theme and its city, while the menu is up
  if (playing && metroC.covering()) { // the platform and the train replace the world; the server keeps the time
    metroC.update(dt);
    pingT -= dt;
    if (pingT <= 0) { pingT = 2; transport?.send({ t: 'ping', c: performance.now() }); }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); metroC.draw(ctx, W, H);
    if (metroC.where === 'platform' && !switching) drawPlatformMap();
    lobbyW.update(false, '', [], 0); zoomBox.classList.remove('on'); // nothing to zoom on a platform or in the coach
    requestAnimationFrame(frame);
    return;
  }
  if (playing && transport) {
    acc += dt;
    let n = 0;
    while (acc >= DT && n < 4) { acc -= DT; n++; sampleInput(); }
    if (acc > DT * 4) acc = 0;
    transport.pump?.(dt * 1000);
    pingT -= dt;
    if (pingT <= 0) { pingT = 2; transport.send({ t: 'ping', c: performance.now() }); }
  }
  draw(Math.max(0, Math.min(1, acc / DT)), dt);
  drawFade();
  requestAnimationFrame(frame);
}

// ------------------------------------------------------------------ menu
// no names (docs/plans/2026-10-19-character-select.md, section 5): online you are "Rider n" until you pick a face at the
// station, and then its name; offline you are your last face's name
async function start(online: boolean) {
  transport?.close();
  if (!online && loadedJson !== gameJson) await setWorld('game'); // practice runs the game's map, wherever you last were
  P.x = P.y = 0; // forces a snap on the first snapshot
  setZoom(1);
  chaloOn = !online && !!geo && new URLSearchParams(location.search).has('chalo');
  myTeam = 'P'; // practice on your own is a protester; online, `welcome` says which side you were drawn
  transport = online ? new Online(roomCode) : new Offline(offlineName(), 'P', {}, myAv);
  if (online) statusEl.textContent = 'Connecting…';
}
function leave(msg: string) {
  if (story) { story = null; phone.hide(); card.hide(); fade.a = 0; fade.then = null; }
  if (roam) roam = null;
  picker.hide(); selectV.hide(); selNo = -1;
  playing = false; transport?.close(); transport = null; snaps = []; mapWin?.close(); setPin(null); showInvite(null); audio.idle();
  room.id = 0; lobbyW.update(false, '', [], 0); metroC.reset(); card.hide();
  menu.classList.remove('hide'); zoomBox.classList.remove('on'); if (msg) statusEl.textContent = msg; else refreshStatus();
  sndShow(); // N in the game may have changed it
}
/**
 * The browser's Back button in a game: the game is one page, so without its own history entry, Back would skip the
 * game and land on whatever page came before (often /maker). So entering a game (the menu hiding) pushes an entry,
 * Back from a game (popstate) leaves it to the menu, and leaving any other way (Esc, Leave room) pops the entry so
 * history stays one step deep.
 */
let inGameEntry = false;
new MutationObserver(() => {
  const hidden = menu.classList.contains('hide');
  if (hidden && !inGameEntry) { history.pushState({ dharnaGame: true }, ''); inGameEntry = true; }
  else if (!hidden && inGameEntry) { inGameEntry = false; if (history.state?.dharnaGame) history.back(); }
}).observe(menu, { attributes: true, attributeFilter: ['class'] });
addEventListener('popstate', () => {
  if (!inGameEntry || history.state?.dharnaGame) return;
  inGameEntry = false; // the entry is already gone: leaving must not pop another
  leave('');
});

/** Join the march: straight into a room. The feed waits with you there (waitingForRoom), not in front of the door. */
function join() {
  if (playing || playBtn.disabled) return;
  start(true);
}
/**
 * Waiting for your room to fill, the time the docked feed fills: a public room's lobby before the whistle, or the
 * shared metro before the train is timed (it is timed the moment the room is full). Not a coded room of friends, who
 * came to play together, and not story, roam or practice.
 */
function waitingForRoom(): boolean {
  if (!playing || story || roam || roomCode || selectV.open || !(transport instanceof Online)) return false; // not while you pick a face
  if (metroC.where === 'station' || metroC.where === 'platform') return metroC.doorsIn() === null;
  return room.id > 0 && hud.ph === 'lobby';
}
playBtn.onclick = join;
roamBtn.onclick = () => void openRoam();
const notifyForm = $<HTMLFormElement>('notify-form'), notifyMsg = $('notify-msg');
notifyForm.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = (notifyForm.elements.namedItem('email') as HTMLInputElement).value.trim();
  const consent = (notifyForm.elements.namedItem('consent') as HTMLInputElement).checked;
  const say = (text: string, ok: boolean) => { notifyMsg.textContent = text; notifyMsg.className = ok ? 'ok' : 'err'; };
  if (!consent || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { say('That does not look like an email address, or the box is unticked.', false); return; }
  try {
    const r = await fetch('/notify', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, consent: true }) });
    if (r.status === 201) { say('Done. One email when the next episode is out.', true); notifyForm.reset(); }
    else if (r.status === 200) say('You are already on the list.', true);
    else if (r.status === 400) say('That does not look like an email address, or the box is unticked.', false);
    else say('Could not reach the server. Try again in a minute.', false);
  } catch { say('Could not reach the server. Try again in a minute.', false); }
});
// a room of your own: a fresh code in the address bar, so the page's own link is the invite
friendsBtn.onclick = () => {
  roomCode = newRoomCode();
  history.replaceState(null, '', `/play?room=${roomCode}`);
  menuForRoom(); start(true);
};
inviteCopy.onclick = () => {
  if (!roomCode) return;
  const done = () => { inviteCopy.textContent = 'Link copied'; setTimeout(() => { inviteCopy.textContent = 'Copy invite link'; }, 2000); };
  navigator.clipboard?.writeText(inviteLink(roomCode)).then(done, () => { inviteCode.textContent = inviteLink(roomCode!); });
};
/** the in-game chip naming the room, while in a room of friends */
function showInvite(code: string | null) {
  inviteEl.classList.toggle('on', !!code);
  if (code) inviteCode.textContent = `Room ${code.toUpperCase()}`;
}
/** with a room code, Play joins the room and "Play with friends" is not offered (the code is already the room) */
function menuForRoom() {
  playBtn.textContent = roomCode ? `Join room ${roomCode.toUpperCase()}` : 'Join the march';
  friendsBtn.hidden = !!roomCode;
}

function refreshStatus() {
  fetch('/status', { cache: 'no-store' }).then((r) => r.json())
    .then((s) => {
      statusEl.textContent = roomCode ? `Server live · invited to room ${roomCode.toUpperCase()}` : `Server live · ${s.players} online · ${s.phase}`;
      statusEl.classList.add('live'); playBtn.disabled = friendsBtn.disabled = false; cityStatus(s);
    })
    .catch(() => { statusEl.textContent = 'Server unreachable. Roaming the city still works offline.'; statusEl.classList.remove('live'); playBtn.disabled = friendsBtn.disabled = true; cityStatus(null); });
}

/** The title screen's Season 1 rail, from the same manifest the landing page reads (data/episodes.json). A playable
 *  episode links to its `play` URL and shows how the last run went; the rest show as locked. */
interface Episode { id: string; season?: number; number?: number; title: string; place: string; status: string; premise: string; playable?: boolean; play?: string }
async function loadSeason() {
  let eps: Episode[];
  try { eps = ((await fetchJson<{ episodes: Episode[] }>('/episodes.json')).episodes || []).filter((e) => e.season === 1); } catch { return; }
  if (!eps.length) return;
  const rail = $('eps'); rail.textContent = '';
  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text = '') => { const e = document.createElement(tag); e.className = cls; e.textContent = text; return e; };
  const BY: Record<string, string> = { metro: 'by metro', cab: 'by cab', walk: 'on foot' };
  for (const ep of eps) {
    const open = !!(ep.playable && ep.play && ep.play.startsWith('/play?'));
    const card = open ? Object.assign(el('a', 'ep'), { href: ep.play! }) : el('div', 'ep locked');
    let last: { option?: string } | null = null;
    try { last = JSON.parse(store.get(lastKey(ep.id), 'null')); } catch { /* none */ }
    card.append(el('span', 'no', String(ep.number ?? '').padStart(2, '0')), el('b', '', ep.title), el('small', '', open ? ep.place : ep.premise));
    if (open && last?.option) card.append(el('small', 'last', `Last run: ${BY[last.option] ?? last.option}`));
    card.append(el('span', 'go', open ? (last ? 'Play again ▶' : 'Play ▶') : '🔒 In development'));
    rail.append(card);
  }
  // the season plan has more episodes than the manifest lists yet: one locked card stands for them
  const more = el('div', 'ep locked');
  more.append(el('span', 'no', String((eps.at(-1)!.number ?? eps.length) + 1).padStart(2, '0')), el('b', '', 'More episodes'), el('small', '', 'The rest of Season 1 is being built.'), el('span', 'go', '🔒 In development'));
  rail.append(more);
}

/** flowDir on a plain BFS field to the zone: what the browser check measures the guidance arrow against. */
function zoneFlow(x: number, y: number): Vec | null {
  const F = fieldFor(map, 'zone', () => {
    const out: Vec[] = [];
    for (let ty = 0; ty < map.h; ty++) for (let tx = 0; tx < map.w; tx++) if (map.rows[ty][tx] === 'P') out.push({ x: (tx + 0.5) * map.tile, y: (ty + 0.5) * map.tile });
    return out;
  });
  return flowDir(map, F, x, y);
}

// read-only debug hook for browser tests: only exists when the page is opened with ?debug
if (location.search.includes('debug')) {
  (window as any).__convoy = () => convoy; // screenshot scripts drive the van off by hand,
  (window as any).__tp = (x: number, y: number) => transport?.send({ t: 'dbg', a: 'tp', x, y }); // and stand offline anywhere
  (window as any).__kafila = () => ({ gateAlerts, metro: { ...metroC.state(), next: metroGate.path[Math.min(2, metroGate.path.length - 1)] ?? null }, x: P.x, y: P.y, meId, mapName: map.name, switching, drop: myDrop, room: room.id, team: myTeam, phase: hud.ph, round: hud.round, pending: pending.length, jog: P.jog, corr, offCentre: Math.hypot(drawn.x - cam.x, drawn.y - cam.y) * cam.z, zoom: zoomMul, ghee: { feet: stages ? gheeUnderFeet(P.st, stages.jog.minToStart) : null, meter: !!stages && playing }, steer: !!(cursor() || (stick && stick.d > 0)), snaps: snaps.length, ping, playing, names: [...names.values()], pockets: map.pockets,
    cast: castSeen, copies: people ? people.copies : 0, sg: stage.sg, sv: stage.sv, sgt: stage.sgt, k: stage.k, st: P.st, pxPerMetre: cam.z * map.tile / (map.mpt || 1), onScreen, frameMs,
    dist: view ? view.distanceM(stage.sv, P.x, P.y) : null, way: view ? view.way(stage.sv, P.x, P.y) : null,
    flow: view ? zoneFlow(P.x, P.y) : null, ahead: view ? view.ahead(stage.sv, P.x, P.y, 3) : null, hint: !!stages && pushT >= stages.bounds.hintAfterS, line: view && stage.sg >= 0 ? stageLine() : '',
    cam: { x: cam.x, y: cam.y, z: cam.z },
    cars: traffic ? traffic.sim.cars.map((c) => ({ x: c.x, y: c.y, v: c.v, a: c.alpha })) : [],
    roam: roam && { id: roam.at.id, name: roam.at.name }, picker: picker.open, sel: selectV.open, av: myAv,
    chalo: chaloOn, ch: chs, held: meHeld, sit: meSit, talk: myTalk(), props: world ? world.drawn : 0, mapOpen: !!mapWin?.open, pin, mini: mini?.rect, checklist: chaloOn && geo && chs ? chaloLines().map(({ head, body, done }) => ({ head, body, done })) : null,
    friends: snaps.length ? [...snaps[snaps.length - 1].rows.values()].filter((r) => r[1] === 4).length : 0,
    emotes: cues ? cues.emotes() : [], blackout: dark(), audio: audio.debug(),
    story: story && { scene: story.scene, phase: story.js.phase, choices: story.js.choices, goal: story.goal, next: story.path[Math.min(3, story.path.length - 1)] ?? null,
      clock: clock(storyClock()), ride: story.ride?.state() ?? null, arr: story.arr, phone: phone.open, card: card.open, stall: story.stall, nearStall: nearStall(), bazaar: bazaar.open,
      cab: story.call ? { arrived: story.call.arrived, x: story.call.car.x, y: story.call.car.y } : null, fade: fade.a, free: meFree, map: map.name } });
}

// ------------------------------------------------------------------ boot
/**
 * Build everything drawn from a map: its layers, the minimap and map window, and (with `extras`) its stages, rings and
 * surround from the server. Boot calls it once; story mode calls it again for each scene's map. `before` sees the
 * loaded map before the layers are made (the doorstep renames its shop there).
 */
async function loadWorld(json: MapJson, o: { extras: boolean; before?: (m: GameMap) => void }) {
  map = loadMap(json); loadedJson = json;
  o.before?.(map);
  stages = null; view = null; chaloData = null; geo = null; ringRoads = null; zoneAt = null; surround = null; chs = null; meFree = false;
  world = null; convoy = null; cues = null;
  stage.sg = -1;
  if (o.extras) {
    // the map's stage areas and pacing, if it has them; without, rounds are free-roaming as before
    try { const r = await fetch('/stages.json'); if (r.ok) { stages = (await r.json()) as Stages; view = new StageView(map, stages); } } catch { /* no stages */ }
    try { const r = await fetch('/chalo.json'); if (r.ok) { chaloData = (await r.json()) as ChaloData; geo = chaloGeo(map, chaloData); world = new ChaloWorld(map, geo, chaloData); audio.setChalo(chaloData.rules, geo, map.mpt ? map.tile / map.mpt : 15); } } catch { /* no rings */ }
    if (chaloData) convoy = new Convoy(map, chaloData.rings, (chaloData.rules as { march?: { vanLeaves?: string } }).march?.vanLeaves);
  }
  SPEED = speedFor(map); PLAYER_R = P.r = radiusFor(map, 'player'); NPC_R = radiusFor(map, 'npc');
  resize();
  blocks = BuildingLayer.from(map); compounds = CompoundLayer.from(map); traffic = TrafficLayer.from(map); layer = new TileLayer(map, blocks); trees = new TreeLayer(map); dressing = Dressing.for(map); people = new PeopleLayer(map.mpt ? map.tile / map.mpt : 15);
  const art = new MapArt(map, { roadScale: 0.7 }); // a paper strip of pavement between the road's ink and the walls
  mini = new Minimap(map, art);
  if (mapWin) { mapWin.close(); mapWin.onPin = () => {}; } // the old window's listeners stay on the canvas: keep them from dropping pins
  mapWin = new MapWindow(map, art, $('mapwin'), $<HTMLCanvasElement>('mapc'), map.name);
  mapWin.me = () => ({ x: P.x, y: P.y }); mapWin.onPin = setPin;
  setPin(null);
  // scenery only, so it never holds up the game: fetched once the map is in, drawn once it arrives
  if (o.extras) {
    const m0 = map;
    fetch('/surround.json').then((r) => (r.ok ? r.json() : null)).then((j: MapJson | null) => {
      if (j?.inset != null && j.tile === map.tile && map === m0) surround = new Surround(loadMap(j), j.inset, map);
    }).catch(() => { /* no surround */ });
  }
}

// ------------------------------------------------------------------ roam the city (docs/plans/2026-10-19-fixes-round-1.md, section C)
// Card 03: the district map, offline, from a place you pick. No round timer (the round outlasts any session), no police
// (offline you are the only human, on foot), no score; the traffic, the crowd and the HUD's names stay. M picks again.
const picker = new RoamPicker($('roampick'));
/** while roaming, the place you came up at; null outside roam */
let roam: { at: RoamPlace } | null = null;
const fetchText = async (url: string) => { const r = await fetch(url); if (!r.ok) throw new Error(`${url}: ${r.status}`); return r.text(); };
/** From the title: load the district behind the menu, then the picker over it. */
async function openRoam() {
  if (picker.open) return;
  roamBtn.disabled = true;
  try {
    await setWorld('city', ROAM_MAP);
    const j = cityJson.get(ROAM_MAP);
    if (!j || loadedJson !== j) throw new Error('the district map did not load');
    picker.setMap(map, await loadPlaces(j, fetchText));
  } catch (err) { console.error(err); statusEl.textContent = 'Could not load the city map.'; return; } finally { roamBtn.disabled = false; }
  picker.show({ here: null, onPick: roamTo, onClose: () => roamBtn.focus() });
}
/** Start roaming at p, or move there if already roaming: on the street nearest it that joins the rest of the city. */
function roamTo(p: RoamPlace) {
  const spot = roamSpot(map, { x: p.at.x * map.tile, y: p.at.y * map.tile }) ?? map.plaza;
  if (!roam || !transport) {
    transport?.close(); P.x = P.y = 0; setZoom(1); chaloOn = false;
    transport = new Offline(offlineName(), 'P', { roam: true, chalo: null, countdown: 0, roundTime: 1e9, npcs: 60 }, myAv);
  }
  roam = { at: p };
  setPin(null); recentre();
  transport.place?.(spot);
}

// ------------------------------------------------------------------ story mode (docs/plans/2026-10-17-journey.md)
// ?story=s1e1 plays an episode offline: the doorstep (its own map), the ride (a 1D view), then the protest's map with
// the journey's arrival. The journey's rules and time model are in shared/journey.ts; the scenes' pieces in story.ts.

interface StoryRun {
  id: string; J: JourneyData; R: Routes; js: JourneyState;
  scene: 'doorstep' | 'ride' | 'arrival';
  pts: DoorstepPoints | null;
  /** the pickup: the metro gate, or the cab's kerb */
  goal: Vec | null; goalName: string;
  path: Vec[]; pathT: number; path0: number;
  call: Summoned | null; waitT: number;
  ride: RideView | null; rideId: string;
  arr: Arrival | null;
  /** the poster bazaar (null when the episode has none), and its stall on the protest's map */
  B: BazaarData | null; stall: Vec | null;
  /** seconds since the scene began, and when the cab that dropped you off leaves */
  t: number; dropT: number;
}
let story: StoryRun | null = null;
const phone = new Phone($('phone')), card = new JourneyCardView($('jcard')), bazaar = new BazaarView($('bazaar'));
/** A fade to night between scenes: `then` runs at full dark, then it lifts. */
const fade = { a: 0, dir: 0, then: null as null | (() => void | Promise<void>) };
function fadeTo(then: () => void | Promise<void>) { if (!fade.then) { fade.dir = 1; fade.then = then; } }
function drawFade() {
  if (fade.a <= 0) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.fillStyle = `rgba(14,12,9,${Math.min(1, fade.a)})`; ctx.fillRect(0, 0, W, H);
}
const metre = () => (map.mpt ? map.tile / map.mpt : 15);
const lastKey = (id: string) => `kafila_story_${id}`;
const fetchJson = async <T,>(url: string) => { const r = await fetch(url); if (!r.ok) throw new Error(`${url}: ${r.status}`); return (await r.json()) as T; };

async function startStory(id: string) {
  const [J, R, B] = await Promise.all([fetchJson<JourneyData>(`/story/${id}.journey.json`), fetchJson<Routes>(`/story/${id}.routes.json`),
    fetchJson<BazaarData>(`/story/${id}.bazaar.json`).catch(() => null)]); // an episode may have no bazaar
  const name = J.doorstep.map.replace(/^.*\//, '').replace(/\.json$/, '');
  const raw = await fetchJson<MapJson>(`/maps/${name}.json`);
  // the walk's gate is the routes file's nearest one; its OSM node is the walk leg's destination
  const prep = prepareDoorstep(raw, R[nearestGate(R)].to);
  let pts: DoorstepPoints | null = null;
  await loadWorld(prep.json, { extras: false, before: (m) => { pts = prep.points(m); } });
  story = { id, J, R, js: startJourney(), scene: 'doorstep', pts, goal: null, goalName: '', path: [], pathT: 0, path0: 1, call: null, waitT: 0, ride: null, rideId: '', arr: null, B, stall: null, t: 0, dropT: -1 };
  transport?.close();
  P.x = P.y = 0; setZoom(1); // each story scene starts at 1:1
  transport = new Offline(offlineName(), 'P', { stages: null, countdown: 0.1, roundTime: 1e6, npcs: 24, friends: J.doorstep.friends.count }, myAv);
  myTeam = 'P';
  let last: Choices | null = null;
  try { last = JSON.parse(store.get(lastKey(id), 'null')); } catch { /* none */ }
  // the phone is on screen at second 0, so the first choice comes within 5 s
  phone.show(J, J.doorstep.place.en, choose, last && last.option ? last : null, () => sameAsLast(last!));
}

/** The phone's choice: the way to go, and the path to its pickup. */
function choose(o: OptionId) {
  const s = story!;
  s.js = journeyStep(s.J, s.js, { t: 'choose', option: o });
  phone.hide();
  if (o === 'metro') { s.goal = s.pts!.gate; s.goalName = s.pts!.gateName.replace(/^GTB Nagar Metro /, 'Metro ').replace('No. ', ''); }
  else {
    // a pin on the kerb nearest the PG gate; a fleet sedan drives there through the traffic
    s.call = traffic?.sim.summon(s.pts!.home, 'sunny') ?? null;
    s.goal = s.call ? s.call.kerb : s.pts!.home; s.goalName = 'Your cab';
  }
  s.path = pathLine(map, s.goal!, { x: P.x, y: P.y }); s.path0 = Math.max(1, pathLength(s.path)); s.pathT = 0;
}

/** Replays: straight to the arrival with last time's choices. */
function sameAsLast(c: Choices) {
  const s = story!;
  phone.hide();
  s.js = { phase: 'arrive', choices: { option: c.option, picks: c.picks }, t: 0 }; // the bazaar comes after the arrival: it is chosen again
  fadeTo(toArrival);
}

/** Story keys: the phone (1, 2, 3), the ride's choices (1, 2), the bazaar (E), and closing the journey card. */
function storyKey(code: string): boolean {
  const s = story!;
  if (bazaar.key(code)) return true;
  if (card.open) { card.hide(); return true; }
  if (phone.key(code)) return true;
  if (s.ride) return s.ride.key(code);
  if (code === 'KeyE' && nearStall()) { keys.clear(); bazaar.show(s.B!, takePoster); return true; }
  return false;
}
/**
 * The stall is open from the arrival until you decide, whatever the stage: Gather can be over 9 s after a late
 * arrival, before the journey card is even closed. Every stage's area holds Gather's, so it stays in reach.
 */
const stallOpen = () => {
  const s = story;
  return !!s && s.scene === 'arrival' && !!s.stall && s.js.choices.poster === undefined && hud.ph === 'playing';
};
const nearStall = () => stallOpen() && Math.hypot(P.x - story!.stall!.x, P.y - story!.stall!.y) < 6 * metre();

/** The bazaar's pick: carry the poster (its minutes run the Gather on without you), or walk past for nothing. */
function takePoster(slug: string | null) {
  const s = story!;
  s.js = journeyStep(s.J, s.js, { t: 'poster', slug });
  if (s.arr) s.arr = { ...s.arr, carry: carryOf(s.js.choices) };
  transport?.spend?.(bazaarMin(s.B!, s.js.choices) * s.J.clock.lateGatherSecPerMin);
}

/** The clock now, in minutes of the day: the time model's minutes squeezed into the scene's seconds. */
function storyClock(): number {
  const s = story!, J = s.J, c = s.js.choices, leave = toMin(J.clock.leaveAt);
  if (s.scene === 'ride' && s.ride) return s.ride.clockMin();
  // the protest's minutes run on, slowly; the stall's minutes on top
  if (s.scene === 'arrival' && s.arr) return toMin(s.arr.arriveAt) + (s.B ? bazaarMin(s.B, c) : 0) + s.t / 60 * 4;
  if (s.js.phase === 'phone' || !s.goal) return leave;
  if (c.option === 'metro') { const walk = parts(J, s.R, c)[0].min, done = 1 - Math.min(1, pathLength(s.path) / s.path0); return leave + walk * done; }
  return leave + J.model.cab.etaMin * Math.min(1, s.call ? s.waitT / Math.max(1, s.call.etaS) : 1);
}

function storyTopLine(): string {
  const s = story!;
  return s.scene === 'arrival' ? `${clock(storyClock())}   ·   Jantar Mantar` : `${clock(storyClock())}   ·   ${s.J.doorstep.place.en}`;
}

/** Ride times from the time model: boarding, the end of the ride, the arrival, and how late against the meeting. */
function rideTimes() {
  const s = story!, ps = parts(s.J, s.R, s.js.choices), leave = toMin(s.J.clock.leaveAt);
  const r = ps.findIndex((p) => p.what === 'ride');
  const start = leave + ps.slice(0, r).reduce((a, p) => a + p.min, 0);
  const end = start + ps[r].min + ps.filter((p) => p.what === 'event').reduce((a, p) => a + p.min, 0);
  const a = arrival(s.J, s.R, s.js.choices, stages ?? undefined);
  return { start, end, arrive: toMin(s.J.clock.meetAt) + a.lateMin, late: a.lateMin };
}

function toRide() {
  const s = story!;
  s.js = journeyStep(s.J, s.js, { t: 'boarded' });
  s.rideId = s.J.options.find((o) => o.id === s.js.choices.option)!.ride;
  transport?.close(); transport = null; snaps = [];
  traffic?.sim.leave();
  s.scene = 'ride'; s.t = 0;
  announcer.reset();
  s.ride = new RideView(s.J, s.R, s.rideId, {
    times: rideTimes,
    pick: (event, choice) => { s.js = journeyStep(s.J, s.js, { t: 'pick', event, choice }); },
    swap: (ride) => { s.rideId = ride; },
    done: () => fadeTo(toArrival),
    companions: () => s.J.doorstep.friends.count + Object.entries(s.js.choices.picks).reduce((a, [e, c]) => a + (s.J.events.find((x) => x.id === e)?.choices.find((x) => x.id === c)?.effect.companions ?? 0), 0),
    chime,
    say: (id, prio) => { announcer.say(id, prio); },
  }, () => s.js.choices.picks);
}

/** The protest's map, with the journey's arrival: where, how late, who with. */
async function toArrival() {
  const s = story!;
  s.js = journeyStep(s.J, s.js, { t: 'rideDone' });
  announcer.stop(true); s.ride = null; s.scene = 'arrival'; // the line the ride ends on winds down, not cut mid-word s.t = 0;
  store.set(lastKey(s.id), JSON.stringify(s.js.choices));
  await loadWorld(await fetchJson<MapJson>('/map.json'), { extras: true });
  chaloData = null; geo = null; // story mode plays the stages, not Chalo
  const a = arrival(s.J, s.R, s.js.choices, stages ?? undefined);
  s.arr = a;
  const at = markerPoint(map, s.J.arrivals[a.marker].marker);
  s.stall = s.B ? stallPoint(map, markerPoint(map, s.B.site.marker), map.plaza, s.B.site.inM) : null;
  const start = stages ? stages.starts.findIndex((x) => x.name === s.J.clock.meetPlace) : -1;
  transport?.close(); P.x = P.y = 0; setZoom(1);
  transport = new Offline(offlineName(), 'P', {
    countdown: 0.1, ...(start >= 0 ? { start } : {}),
    arrival: { playerAt: at, stage: a.stage === 'march' ? 1 : 0, stageT: a.stageT, companions: a.companions, skipGather: a.skipGather },
  }, myAv);
  // by cab: a fleet car is parked at the kerb with its doors open, then drives off
  if (a.by === 'cab' && traffic) { traffic.sim.summon(at, 'sunny', 0, true); s.dropT = 2.5; }
  const met = s.J.events.find((e) => e.kind === 'rumour' && s.js.choices.picks[e.id]);
  card.show(`${journeyCard(s.J, a)} · ${lateText(a.lateMin)}`, stageText(a, stages?.gather.maxS ?? 45), met?.debrief ?? null);
}

function storyUpdate(dt: number) {
  const s = story!;
  // the fade between scenes
  if (fade.dir > 0) { fade.a = Math.min(1, fade.a + dt / 0.5); if (fade.a >= 1 && fade.then) { const f = fade.then; fade.then = null; fade.dir = 0; void Promise.resolve(f()).then(() => { fade.dir = -1; }); } }
  else if (fade.dir < 0) { fade.a = Math.max(0, fade.a - dt / 0.6); if (!fade.a) fade.dir = 0; }
  s.t += dt;
  card.update(dt);
  if (s.scene === 'arrival') {
    if (s.dropT > 0 && (s.dropT -= dt) <= 0) traffic?.sim.leave();
    return;
  }
  if (s.scene !== 'doorstep' || !s.goal || fade.then) return;
  // the path hint follows you
  if ((s.pathT -= dt) <= 0) { s.pathT = 0.4; s.path = pathLine(map, s.goal, { x: P.x, y: P.y }); }
  const d = Math.hypot(P.x - s.goal.x, P.y - s.goal.y), m = metre();
  if (s.js.choices.option === 'metro') {
    if (s.js.phase === 'toPickup' && d < 6 * m) { s.js = journeyStep(s.J, s.js, { t: 'atPickup' }); fadeTo(toRide); }
    return;
  }
  // the cab: walk to the pin, wait for the car, board within 6 m
  if (s.js.phase !== 'phone') s.waitT += dt;
  if (s.js.phase === 'toPickup' && d < 8 * m) s.js = journeyStep(s.J, s.js, { t: 'atPickup' });
  const here = !s.call || s.call.arrived;
  if (s.js.phase === 'wait' && here) s.js = journeyStep(s.J, s.js, { t: 'carHere' });
  if (s.js.phase === 'board' && here && Math.hypot(P.x - (s.call?.car.x ?? s.goal.x), P.y - (s.call?.car.y ?? s.goal.y)) < 8 * m) fadeTo(toRide);
}

/** The shared metro at a station: the way to the nearest gate of your station, refreshed as you walk. */
const metroGate = { t: 0, map: '', gates: [] as Vec[], path: [] as Vec[] };
function metroWorld(z: number) {
  const now = performance.now() / 1000;
  if (metroGate.map !== map.name) { metroGate.map = map.name; metroGate.gates = metroC.gates(map); metroGate.t = 0; }
  if (!metroGate.gates.length) return;
  // the nearest by walking: a gate across a fence is no use, however close it looks
  const near = nearestStationGate(map, metroGate.gates, P) ?? metroGate.gates[0];
  if (now - metroGate.t > 0.4) { metroGate.t = now; metroGate.path = pathLine(map, near, { x: P.x, y: P.y }); }
  drawPath(ctx, metroGate.path, z, now);
  for (const g of metroGate.gates) drawMarker(ctx, g, g === near ? `${metroC.stn?.name ?? 'Metro'} · gate` : '', z, '#2F7DE1');
}

/** Story's ground marks: at the doorstep the path to the pickup and the pickup; on arrival the bazaar's stall. World coordinates. */
function storyWorld(z: number, mx: number, my: number) {
  const s = story!;
  if (s.scene === 'arrival') { if (stallOpen()) drawMarker(ctx, s.stall!, 'Posters', z, INK.newsprint); return; }
  if (s.goal) { drawPath(ctx, s.path, z, performance.now() / 1000); drawMarker(ctx, s.goal, s.goalName, z, s.js.choices.option === 'metro' ? INK.metro : INK.newsprint); }
  void mx; void my;
}

/** The doorstep's line under the clock: what to do now. Screen px. */
function storyHud() {
  const s = story!, m = metre();
  let msg = '';
  if (s.js.phase === 'phone') msg = 'Your phone buzzes';
  else if (s.js.phase === 'toPickup') msg = `Walk to ${s.goalName} · ${Math.round(pathLength(s.path) / m)} m`;
  else if (s.js.phase === 'wait') { const left = Math.ceil((s.call?.etaS ?? 0) - s.waitT); msg = left > 0 ? `Your cab is ${left} s away` : 'Your cab is almost here'; }
  else if (s.js.phase === 'board') msg = 'Your cab is here. Get in';
  hudClock(clock(storyClock()), msg);
}

(async function boot() {
  menuForRoom();
  resize();
  const storyId = new URLSearchParams(location.search).get('story');
  if (storyId && /^[a-z0-9-]+$/.test(storyId)) {
    menu.classList.add('hide');
    try { await startStory(storyId); } catch (err) { story = null; menu.classList.remove('hide'); statusEl.textContent = `Story ${storyId} could not load.`; console.error(err); }
  }
  if (!story) { gameJson = (await (await fetch('/map.json')).json()) as MapJson; await loadWorld(gameJson, { extras: true }); gameStages = stages; }
  refreshStatus(); void loadSeason();
  requestAnimationFrame(frame);
  // ?offline=1 skips the title screen and starts practice (an old link, and the unreachable-server path)
  if (!story && new URLSearchParams(location.search).has('offline')) start(false);
})();

